import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db, storage, auth } from '../firebase/config';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
} from 'firebase/firestore';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  listAll,
} from 'firebase/storage';
import './UploadManager.css';

function UploadManager({ user }) {
  const [unavailableAlbums, setUnavailableAlbums] = useState([]);
  const [allAlbums, setAllAlbums] = useState([]); // All albums for filter dropdowns
  const [selectedAlbums, setSelectedAlbums] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    language: 'all',
    genre: 'all',
    subgenre: 'all',
    artist: 'all',
    album: 'all',
    type: 'all',
  });
  const [groupBy, setGroupBy] = useState('language'); // 'language', 'genre', 'artist'
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadStatus, setUploadStatus] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState(null);
  const [syncProgress, setSyncProgress] = useState({
    current: 0,
    total: 0,
    currentAlbum: '',
  });
  const syncCancelledRef = useRef(false);

  // Function to mark all 60s Decades albums as unavailable
  const markAll60sAlbumsUnavailable = async () => {
    try {
      console.log('🔧 Marking all 60s Decades albums as unavailable...');

      // Query albums with matching criteria
      const q = query(
        collection(db, 'albums-flat'),
        where('type', '==', 'Albums'),
        where('language', '==', 'English'),
        where('genre', '==', 'Decades')
      );

      const querySnapshot = await getDocs(q);
      const allDecadesAlbums = [];
      querySnapshot.forEach((doc) => {
        allDecadesAlbums.push({ id: doc.id, ...doc.data() });
      });

      // Filter for 60s subgenre or albums with "Sixties" in name
      const albums = allDecadesAlbums.filter(
        (a) =>
          a.subgenre === '60s' ||
          a.subgenre === 'Various Artists' ||
          a.album?.includes('Sixties')
      );

      console.log(`Found ${albums.length} albums to update`);

      let updatedCount = 0;
      let errorCount = 0;
      const errors = [];

      for (const album of albums) {
        try {
          await updateDoc(doc(db, 'albums-flat', album.id), {
            storageStatus: 'unavailable',
            syncedAt: new Date().toISOString(),
          });
          updatedCount++;
          console.log(`  ✅ Updated: ${album.album} → unavailable`);
        } catch (updateError) {
          errorCount++;
          const errorMsg =
            updateError.message || updateError.code || 'Unknown error';
          errors.push(`${album.album}: ${errorMsg}`);
          console.error(`  ❌ Error updating ${album.album}: ${errorMsg}`);
        }
      }

      console.log(`\n📊 Update Summary:`);
      console.log(`   Updated: ${updatedCount}`);
      console.log(`   Errors: ${errorCount}`);

      if (errors.length > 0) {
        console.log('\n⚠️ Errors:');
        errors.forEach((err) => console.log(`  - ${err}`));
      }

      alert(
        `Updated ${updatedCount} albums to unavailable.${
          errorCount > 0 ? ` ${errorCount} errors.` : ''
        } Check console for details.`
      );

      // Reload unavailable albums
      const unavailableQuery = query(
        collection(db, 'albums-flat'),
        where('storageStatus', '==', 'unavailable')
      );
      const unavailableSnapshot = await getDocs(unavailableQuery);
      const unavailableAlbumsList = [];
      unavailableSnapshot.forEach((doc) => {
        unavailableAlbumsList.push({ id: doc.id, ...doc.data() });
      });
      setUnavailableAlbums(unavailableAlbumsList);
    } catch (error) {
      console.error('Error marking albums as unavailable:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Diagnostic function to check albums in a specific path
  const checkDecadesAlbums = async () => {
    try {
      console.log(
        '🔍 Checking albums in Albums/English/Decades/60s/Various Artists/...'
      );

      // Query all Albums/English/Decades albums (we'll filter by subgenre in code)
      const q = query(
        collection(db, 'albums-flat'),
        where('type', '==', 'Albums'),
        where('language', '==', 'English'),
        where('genre', '==', 'Decades')
      );

      const querySnapshot = await getDocs(q);
      const allDecadesAlbums = [];
      querySnapshot.forEach((doc) => {
        allDecadesAlbums.push({ id: doc.id, ...doc.data() });
      });

      // Filter for 60s subgenre
      const albums = allDecadesAlbums.filter(
        (a) =>
          a.subgenre === '60s' ||
          a.subgenre === 'Various Artists' ||
          a.album?.includes('Sixties')
      );

      console.log(`\n📊 Found ${albums.length} albums matching criteria:`);
      console.log('   Type: Albums');
      console.log('   Language: English');
      console.log('   Genre: Decades');
      console.log(
        '   Subgenre: 60s or Various Artists (or album name contains "Sixties")'
      );
      console.log(
        `\n   (Total Decades albums found: ${allDecadesAlbums.length})`
      );
      console.log('\n📋 Album Details:');

      albums.forEach((album, index) => {
        console.log(`\n${index + 1}. ${album.album || 'Unknown Album'}`);
        console.log(`   Artist: ${album.artist || album.artistName || 'N/A'}`);
        console.log(`   Genre: ${album.genre || 'N/A'}`);
        console.log(`   Subgenre: ${album.subgenre || 'N/A'}`);
        console.log(`   Storage Status: ${album.storageStatus || 'NOT SET'}`);
        console.log(`   Cover URL: ${album.coverUrl || 'NOT SET'}`);
        console.log(`   Document ID: ${album.id}`);
      });

      // Count by status
      const availableCount = albums.filter(
        (a) => a.storageStatus === 'available'
      ).length;
      const unavailableCount = albums.filter(
        (a) => a.storageStatus === 'unavailable'
      ).length;
      const noStatusCount = albums.filter((a) => !a.storageStatus).length;

      console.log(`\n📈 Status Summary:`);
      console.log(`   Available: ${availableCount}`);
      console.log(`   Unavailable: ${unavailableCount}`);
      console.log(`   No Status: ${noStatusCount}`);
      console.log(`   Total: ${albums.length}`);

      // Identify albums incorrectly marked as available
      const incorrectlyAvailable = albums.filter(
        (a) => a.storageStatus === 'available'
      );
      if (incorrectlyAvailable.length > 0) {
        console.log(
          `\n⚠️ ${incorrectlyAvailable.length} album(s) incorrectly marked as "available":`
        );
        incorrectlyAvailable.forEach((album) => {
          console.log(`   - ${album.album} (ID: ${album.id})`);
        });
        console.log(
          '\n💡 These albums only have album.jpg but no MP3 files. They should be marked as "unavailable".'
        );
        console.log(
          '   Click "Mark All 60s as Unavailable" button to fix, or run "Sync to Docs" to verify storage.'
        );
      }

      // Show in alert too
      const statusMsg = `Available: ${availableCount}, Unavailable: ${unavailableCount}, No Status: ${noStatusCount}`;
      const alertMsg =
        incorrectlyAvailable.length > 0
          ? `Found ${albums.length} albums.\n${statusMsg}\n\n⚠️ ${incorrectlyAvailable.length} album(s) incorrectly marked as "available" (only have album.jpg, no MP3 files).\n\nClick "Mark All 60s as Unavailable" to fix.`
          : `Found ${albums.length} albums.\n${statusMsg}\n\nCheck console for full details.`;
      alert(alertMsg);
    } catch (error) {
      console.error('Error checking Decades albums:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Helper function to construct album storage path
  const getAlbumStoragePath = (album, includeFilename = '') => {
    if (album.type === 'Singles') {
      // Special handling for Decades albums
      if (album.genre === 'Decades' || album.subgenre === 'Various Artists') {
        return `Singles/${album.language}/Decades/Various Artists/${
          album.album
        }${includeFilename ? '/' + includeFilename : '/'}`;
      } else {
        // Standard Singles path
        return `Singles/${album.language}/Various/Variety/Mix/${album.album}${
          includeFilename ? '/' + includeFilename : '/'
        }`;
      }
    } else {
      // Albums - special handling for Decades genre (skip artist folder)
      if (album.genre === 'Decades') {
        // Path: Albums/English/Decades/Subgenre/AlbumName/
        return `${album.type}/${album.language}/${album.genre}/${
          album.subgenre
        }/${album.album}${includeFilename ? '/' + includeFilename : '/'}`;
      } else {
        // Standard Albums path
        return `${album.type}/${album.language}/${album.genre}/${
          album.subgenre
        }/${album.artist}/${album.album}${
          includeFilename ? '/' + includeFilename : '/'
        }`;
      }
    }
  };

  // Load unavailable albums
  useEffect(() => {
    const loadUnavailableAlbums = async (retryCount = 0) => {
      // Wait for user to be available
      if (!user) {
        console.log('⏳ Waiting for user prop...');
        setLoading(false);
        return;
      }

      // Wait for auth.currentUser to be ready (may lag behind user prop)
      let currentUser = auth.currentUser;
      if (!currentUser) {
        // Wait a bit for auth to initialize
        await new Promise((resolve) => setTimeout(resolve, 500));
        currentUser = auth.currentUser;
      }

      if (!currentUser) {
        console.log('⏳ Waiting for auth.currentUser...');
        if (retryCount < 3) {
          // Retry after a delay
          setTimeout(() => loadUnavailableAlbums(retryCount + 1), 1000);
          return;
        } else {
          console.error('❌ Auth not ready after retries');
          setLoading(false);
          return;
        }
      }

      setLoading(true);
      try {
        // Ensure auth token is ready
        const token = await currentUser.getIdToken();
        if (!token) {
          throw new Error('No auth token available');
        }

        console.log(`🔍 Querying albums-flat with user: ${currentUser.email}`);

        // First, try to get albums with storageStatus='unavailable'
        const q = query(
          collection(db, 'albums-flat'),
          where('storageStatus', '==', 'unavailable')
        );
        const querySnapshot = await getDocs(q);

        const albums = [];
        querySnapshot.forEach((doc) => {
          albums.push({ id: doc.id, ...doc.data() });
        });

        console.log(`✅ Loaded ${albums.length} unavailable albums`);

        // Always load ALL albums for filter dropdowns
        const allAlbumsQuery = query(collection(db, 'albums-flat'));
        const allAlbumsSnapshot = await getDocs(allAlbumsQuery);

        const allAlbumsList = [];
        allAlbumsSnapshot.forEach((doc) => {
          allAlbumsList.push({ id: doc.id, ...doc.data() });
        });

        console.log(
          `📚 Loaded ${allAlbumsList.length} total albums for filters`
        );
        setAllAlbums(allAlbumsList);

        // If no unavailable albums found, check if storageStatus field exists at all
        if (albums.length === 0) {
          console.log(
            '⚠️ No albums with storageStatus="unavailable" found. Checking all albums...'
          );

          let withStatus = 0;
          let withoutStatus = 0;
          const unavailableList = [];

          allAlbumsList.forEach((album) => {
            if (album.storageStatus) {
              withStatus++;
              if (album.storageStatus === 'unavailable') {
                unavailableList.push(album);
              }
            } else {
              withoutStatus++;
              // If no storageStatus field, treat as unavailable
              unavailableList.push(album);
            }
          });

          console.log(
            `📊 Albums status check: ${withStatus} with storageStatus, ${withoutStatus} without storageStatus`
          );
          console.log(
            `📦 Showing ${unavailableList.length} albums (unavailable + no status)`
          );

          setUnavailableAlbums(unavailableList);
        } else {
          setUnavailableAlbums(albums);
        }
      } catch (error) {
        console.error('❌ Error loading albums:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          user: user?.email,
          authUser: currentUser?.email,
          hasToken: !!currentUser?.stsTokenManager,
        });
        if (error.code === 'permission-denied') {
          console.error(
            '🔒 Permission denied. User might not be fully authenticated.'
          );
          if (retryCount < 2) {
            // Retry after a delay
            console.log(
              `🔄 Retrying in 1 second... (attempt ${retryCount + 1})`
            );
            setTimeout(() => loadUnavailableAlbums(retryCount + 1), 1000);
            return;
          } else {
            alert(
              'Permission denied. Please refresh the page and make sure you are logged in.'
            );
          }
        } else {
          alert(`Error loading albums: ${error.message}`);
        }
        setUnavailableAlbums([]);
      } finally {
        setLoading(false);
      }
    };

    loadUnavailableAlbums();
  }, [user]);

  // Get filtered albums
  const getFilteredAlbums = () => {
    const filtered = unavailableAlbums.filter((album) => {
      // Debug specific album if it's Fleetwood Mac
      const isDebugAlbum =
        album.artist &&
        album.artist.includes('Fleetwood Mac') &&
        album.album &&
        album.album.includes('Greatest Hits');

      if (filters.type !== 'all') {
        const albumType = (album.type || album.Type || '').toString().trim();
        const filterType = filters.type.toString().trim();
        if (albumType.toLowerCase() !== filterType.toLowerCase()) {
          if (isDebugAlbum)
            console.log('❌ Filtered out by type:', { albumType, filterType });
          return false;
        }
      }
      if (filters.language !== 'all') {
        const albumLang = (album.language || album.Language || '')
          .toString()
          .trim();
        const filterLang = filters.language.toString().trim();
        if (albumLang.toLowerCase() !== filterLang.toLowerCase()) {
          if (isDebugAlbum)
            console.log('❌ Filtered out by language:', {
              albumLang,
              filterLang,
            });
          return false;
        }
      }
      if (filters.genre !== 'all') {
        const albumGenre = (album.genre || album.Genre || '').toString().trim();
        const filterGenre = filters.genre.toString().trim();
        if (albumGenre.toLowerCase() !== filterGenre.toLowerCase()) {
          if (isDebugAlbum)
            console.log('❌ Filtered out by genre:', {
              albumGenre,
              filterGenre,
            });
          return false;
        }
      }
      if (filters.subgenre !== 'all') {
        const albumSubgenre = (album.subgenre || album.Subgenre || '')
          .toString()
          .trim();
        const filterSubgenre = filters.subgenre.toString().trim();
        if (albumSubgenre.toLowerCase() !== filterSubgenre.toLowerCase()) {
          if (isDebugAlbum)
            console.log('❌ Filtered out by subgenre:', {
              albumSubgenre,
              filterSubgenre,
            });
          return false;
        }
      }
      if (filters.artist !== 'all') {
        const albumArtist = (
          album.artist ||
          album.artistName ||
          album.Artist ||
          ''
        )
          .toString()
          .trim();
        const filterArtist = filters.artist.toString().trim();
        if (albumArtist.toLowerCase() !== filterArtist.toLowerCase()) {
          if (isDebugAlbum)
            console.log('❌ Filtered out by artist:', {
              albumArtist,
              filterArtist,
            });
          return false;
        }
      }
      if (filters.album !== 'all') {
        const albumName = (album.album || album.albumName || album.Album || '')
          .toString()
          .trim();
        const filterAlbum = filters.album.toString().trim();
        if (albumName.toLowerCase() !== filterAlbum.toLowerCase()) {
          if (isDebugAlbum)
            console.log('❌ Filtered out by album:', {
              albumName,
              filterAlbum,
            });
          return false;
        }
      }
      if (isDebugAlbum) {
        console.log('✅ Album passed all filters:', {
          album: {
            type: album.type,
            language: album.language,
            genre: album.genre,
            subgenre: album.subgenre,
            artist: album.artist,
          },
          filters,
        });
      }
      return true;
    });

    // Debug: Log filter state and results
    if (Object.values(filters).some((f) => f !== 'all')) {
      console.log('🔍 Filtering albums:', {
        filters,
        totalUnavailable: unavailableAlbums.length,
        filteredCount: filtered.length,
      });

      // Show sample of filtered albums
      if (filtered.length > 0) {
        console.log(
          '✅ Matching albums (first 5):',
          filtered.slice(0, 5).map((a) => ({
            artist: a.artist,
            album: a.album,
            genre: a.genre,
            subgenre: a.subgenre,
            language: a.language,
            type: a.type,
          }))
        );
      } else {
        // Show why albums are being filtered out - show unique genre/subgenre values
        const uniqueGenres = new Set();
        const uniqueSubgenres = new Set();
        unavailableAlbums.forEach((a) => {
          if (a.genre) uniqueGenres.add(a.genre);
          if (a.subgenre) uniqueSubgenres.add(a.subgenre);
        });
        console.log('❌ No albums match filters.');
        console.log(
          '   Available genres in unavailable albums:',
          Array.from(uniqueGenres).sort()
        );
        console.log(
          '   Available subgenres in unavailable albums:',
          Array.from(uniqueSubgenres).sort()
        );
        console.log(
          '   Sample unavailable albums:',
          unavailableAlbums.slice(0, 5).map((a) => ({
            artist: a.artist,
            album: a.album,
            genre: a.genre || '(empty)',
            subgenre: a.subgenre || '(empty)',
            language: a.language,
            type: a.type,
          }))
        );
      }
    }

    return filtered;
  };

  // Get unique values for filters (respects current filter selections)
  const getUniqueValues = (field) => {
    const values = new Set();
    // Use allAlbums for ALL filter dropdowns to show all available options
    // This ensures dropdowns are populated even when there are no unavailable albums in the filtered path
    let filtered = allAlbums.length > 0 ? allAlbums : unavailableAlbums;

    // Apply filters (skip the field we're getting unique values for)
    // Use case-insensitive matching to match getFilteredAlbums logic
    if (filters.type !== 'all' && field !== 'type') {
      filtered = filtered.filter((album) => {
        const albumType = (album.type || album.Type || '').toString().trim();
        const filterType = filters.type.toString().trim();
        return albumType.toLowerCase() === filterType.toLowerCase();
      });
    }
    if (filters.language !== 'all' && field !== 'language') {
      filtered = filtered.filter((album) => {
        const albumLang = (album.language || album.Language || '')
          .toString()
          .trim();
        const filterLang = filters.language.toString().trim();
        return albumLang.toLowerCase() === filterLang.toLowerCase();
      });
    }
    if (filters.genre !== 'all' && field !== 'genre') {
      filtered = filtered.filter((album) => {
        const albumGenre = (album.genre || album.Genre || '').toString().trim();
        const filterGenre = filters.genre.toString().trim();
        return albumGenre.toLowerCase() === filterGenre.toLowerCase();
      });
    }
    if (filters.subgenre !== 'all' && field !== 'subgenre') {
      filtered = filtered.filter((album) => {
        const albumSubgenre = (album.subgenre || album.Subgenre || '')
          .toString()
          .trim();
        const filterSubgenre = filters.subgenre.toString().trim();
        return albumSubgenre.toLowerCase() === filterSubgenre.toLowerCase();
      });
    }
    if (filters.artist !== 'all' && field !== 'artist') {
      filtered = filtered.filter((album) => {
        const albumArtist = (
          album.artist ||
          album.artistName ||
          album.Artist ||
          ''
        )
          .toString()
          .trim();
        const filterArtist = filters.artist.toString().trim();
        return albumArtist.toLowerCase() === filterArtist.toLowerCase();
      });
    }
    if (filters.album !== 'all' && field !== 'album') {
      filtered = filtered.filter((album) => {
        const albumName = (album.album || album.albumName || album.Album || '')
          .toString()
          .trim();
        const filterAlbum = filters.album.toString().trim();
        return albumName.toLowerCase() === filterAlbum.toLowerCase();
      });
    }

    // Extract unique values for the requested field
    filtered.forEach((album) => {
      // Try multiple field name variations
      let value = album[field];
      if (!value) {
        // Try capitalized version
        const capitalized = field.charAt(0).toUpperCase() + field.slice(1);
        value = album[capitalized];
      }
      if (!value && field === 'album') {
        value = album.albumName;
      }
      if (!value && field === 'artist') {
        value = album.artistName;
      }

      // Debug: Log if we can't find the field
      if (!value && filtered.length > 0 && filtered.indexOf(album) === 0) {
        console.log(`⚠️ Field "${field}" not found in album:`, {
          albumKeys: Object.keys(album),
          album: album.album || album.albumName,
          field: field,
        });
      }

      if (
        value &&
        typeof value === 'string' &&
        value !== 'all' &&
        value.trim() !== ''
      ) {
        values.add(value);
      } else if (value && typeof value !== 'string') {
        // Handle non-string values
        values.add(String(value));
      }
    });

    const result = Array.from(values).sort();

    // Debug: Log if no values found
    if (result.length === 0 && filtered.length > 0) {
      console.warn(
        `⚠️ No values found for field "${field}" in ${filtered.length} filtered albums`
      );
      console.log('Sample album keys:', Object.keys(filtered[0]));
      // Show sample albums to debug filtering
      if (field === 'artist') {
        console.log('🔍 Debug: Filtered albums for artist dropdown:', {
          totalFiltered: filtered.length,
          currentFilters: filters,
          sampleAlbums: filtered.slice(0, 3).map((a) => ({
            artist: a.artist || a.artistName || a.Artist,
            type: a.type || a.Type,
            language: a.language || a.Language,
            genre: a.genre || a.Genre,
            subgenre: a.subgenre || a.Subgenre,
            album: a.album || a.albumName,
          })),
        });
      }
    }

    // Debug: Log when getting artist values
    if (field === 'artist') {
      console.log(
        `🎯 getUniqueValues('artist'): Found ${result.length} artists from ${filtered.length} filtered albums`,
        {
          artists: result,
          artistsList: result, // Explicit list for easy inspection
          filters: filters,
          totalAllAlbums: allAlbums.length,
          totalUnavailableAlbums: unavailableAlbums.length,
          sampleFilteredAlbums: filtered.slice(0, 5).map((a) => ({
            artist: a.artist || a.artistName || a.Artist,
            artistField: a.artist,
            artistNameField: a.artistName,
            ArtistField: a.Artist,
            type: a.type || a.Type,
            language: a.language || a.Language,
            genre: a.genre || a.Genre,
            subgenre: a.subgenre || a.Subgenre,
          })),
        }
      );

      // If filtering by Decades and found artists, show them explicitly
      if (filters.genre === 'Decades' && result.length > 0) {
        console.log('✅ Decades genre filter - Artists found:', result);
      }
    }

    return result;
  };

  // Group albums
  const getGroupedAlbums = () => {
    const filtered = getFilteredAlbums();
    const grouped = {};

    filtered.forEach((album) => {
      const key = album[groupBy] || 'Unknown';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(album);
    });

    return grouped;
  };

  // Toggle album selection
  const toggleAlbum = (albumId) => {
    const newSelected = new Set(selectedAlbums);
    if (newSelected.has(albumId)) {
      newSelected.delete(albumId);
    } else {
      newSelected.add(albumId);
    }
    setSelectedAlbums(newSelected);
  };

  // Toggle all in group
  const toggleGroup = (albums) => {
    const newSelected = new Set(selectedAlbums);
    const allSelected = albums.every((a) => newSelected.has(a.id));

    albums.forEach((album) => {
      if (allSelected) {
        newSelected.delete(album.id);
      } else {
        newSelected.add(album.id);
      }
    });

    setSelectedAlbums(newSelected);
  };

  // Select/Deselect all
  const selectAll = () => {
    const filtered = getFilteredAlbums();
    setSelectedAlbums(new Set(filtered.map((a) => a.id)));
  };

  const deselectAll = () => {
    setSelectedAlbums(new Set());
  };

  // Export selected albums
  const exportSelected = () => {
    const selected = unavailableAlbums.filter((a) => selectedAlbums.has(a.id));

    const exportData = selected.map((album) => {
      const fullPath =
        album.type === 'Singles'
          ? `Singles/${album.language}/Various/Variety/${
              album.artist || album.artistName || 'Unknown'
            }/Mix/`
          : `${album.type}/${album.language}/${album.genre}/${album.subgenre}/${album.artist}/${album.album}/`;

      return {
        id: album.id,
        type: album.type,
        language: album.language,
        genre: album.genre,
        subgenre: album.subgenre,
        artist: album.artist,
        album: album.album,
        filePath: album.filePath,
        coverUrl: album.coverUrl,
        fullPath: fullPath,
      };
    });

    // Download as JSON
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `albums-to-upload-${
      new Date().toISOString().split('T')[0]
    }.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log(`Exported ${selected.length} albums`);
  };

  // Copy paths to clipboard
  const copyPaths = () => {
    const selected = unavailableAlbums.filter((a) => selectedAlbums.has(a.id));
    const paths = selected
      .map((album) => {
        if (album.type === 'Singles') {
          return `Singles/${album.language}/Various/Variety/${
            album.artist || album.artistName || 'Unknown'
          }/Mix/`;
        } else {
          return `${album.type}/${album.language}/${album.genre}/${album.subgenre}/${album.artist}/${album.album}/`;
        }
      })
      .join('\n');

    navigator.clipboard.writeText(paths);
    alert(`Copied ${selected.length} paths to clipboard!`);
  };

  // Start upload process
  const startUpload = () => {
    if (selectedAlbums.size === 0) {
      alert('Please select at least one album to upload');
      return;
    }
    setShowUploadModal(true);
  };

  // Recursively upload all album.jpg files from entire library structure
  const handleFullLibraryImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadStatus('Scanning entire library for album.jpg files...');
    setUploadProgress({});

    let uploadedCount = 0;
    let errorCount = 0;

    try {
      // Filter to only album.jpg, cover.jpg, folder.jpg files
      const albumJpgFiles = files.filter((f) => {
        const fileName = f.name.toLowerCase();
        return (
          fileName === 'album.jpg' ||
          fileName === 'cover.jpg' ||
          fileName === 'folder.jpg'
        );
      });

      if (albumJpgFiles.length === 0) {
        alert(
          'No album.jpg files found! Please select your root library folder (e.g., D:\\Jukebox\\Albums\\)'
        );
        setUploading(false);
        return;
      }

      console.log(`📁 Total files selected: ${files.length}`);
      console.log(
        `📁 Found ${albumJpgFiles.length} album.jpg/cover.jpg/folder.jpg files (filtering to Albums/English only)`
      );

      // Debug: Show sample paths to understand structure
      if (albumJpgFiles.length > 0) {
        console.log('\n🔍 Sample file paths (first 5):');
        albumJpgFiles.slice(0, 5).forEach((f, idx) => {
          console.log(
            `   ${idx + 1}. webkitRelativePath: "${
              f.webkitRelativePath || 'N/A'
            }"`
          );
          console.log(`      name: "${f.name}"`);
        });
      }

      // Categorize images by level based on path structure
      const imagesByLevel = {
        type: [], // Albums/album.jpg or Singles/album.jpg (2 parts)
        language: [], // Albums/English/album.jpg (3 parts)
        genre: [], // Albums/English/Rock/album.jpg (4 parts)
        subgenre: [], // Albums/English/Rock/Classic Rock/album.jpg (5 parts)
        artist: [], // Albums/English/Rock/Classic Rock/Beatles/album.jpg (6 parts)
        album: [], // Albums/English/Rock/Classic Rock/Beatles/Abbey Road/album.jpg (7+ parts)
      };

      let skippedCount = 0;
      let skippedPaths = [];

      for (const file of albumJpgFiles) {
        // Handle case where file is directly in selected folder (webkitRelativePath might be empty or just filename)
        let webkitPath = file.webkitRelativePath || '';
        if (!webkitPath || webkitPath === file.name) {
          // File is directly in selected folder, use empty path (will be prepended with Albums/English)
          webkitPath = '';
        }
        webkitPath = webkitPath.replace(/\\/g, '/');

        const pathParts = webkitPath
          .split('/')
          .filter((p) => p && p !== file.name && p !== '');

        // Debug: Log path structure for more files to diagnose
        const fileIndex = albumJpgFiles.indexOf(file);
        if (fileIndex < 10 || (fileIndex < 50 && fileIndex % 10 === 0)) {
          console.log(
            `\n🔍 Processing file ${fileIndex + 1}/${albumJpgFiles.length}: ${
              file.name
            }`
          );
          console.log(
            `   Original webkitRelativePath: "${
              file.webkitRelativePath || 'N/A'
            }"`
          );
          console.log(`   Processed webkitPath: "${webkitPath}"`);
          console.log(`   pathParts: [${pathParts.join(', ')}]`);
          console.log(`   pathParts.length: ${pathParts.length}`);
        }

        // FILTER: Only process Albums/English paths
        // Handle two cases:
        // 1. User selected Albums/English folder → paths are relative (e.g., "Rock/album.jpg")
        // 2. User selected parent folder (e.g., Albums) → paths include "English" (e.g., "English/Rock/album.jpg")
        const hasAlbumsEnglish =
          pathParts.length >= 2 &&
          pathParts[0] === 'Albums' &&
          pathParts[1] === 'English';

        const startsWithEnglish =
          pathParts.length >= 1 && pathParts[0] === 'English';

        // Build final path parts
        let finalPathParts = pathParts;
        let finalPath = webkitPath;

        if (hasAlbumsEnglish) {
          // Path already includes Albums/English (user selected Albums or higher)
          finalPathParts = pathParts;
          finalPath = webkitPath;
        } else if (startsWithEnglish) {
          // Path starts with English (user selected Albums folder)
          // Prepend "Albums" to get Albums/English/...
          finalPathParts = ['Albums', ...pathParts];
          finalPath = 'Albums/' + webkitPath;
        } else {
          // Path is relative (user selected Albums/English folder)
          // Prepend "Albums/English" to the path
          finalPathParts = ['Albums', 'English', ...pathParts];
          finalPath = 'Albums/English/' + webkitPath;
        }

        // FILTER: Only process files in Albums/English (skip Singles, other languages, etc.)
        if (
          finalPathParts.length < 2 ||
          finalPathParts[0] !== 'Albums' ||
          finalPathParts[1] !== 'English'
        ) {
          skippedCount++;
          if (skippedPaths.length < 5) {
            skippedPaths.push(finalPath);
          }
          continue; // Skip files not in Albums/English
        }

        if (albumJpgFiles.indexOf(file) < 3) {
          console.log(`   Final pathParts: [${finalPathParts.join(', ')}]`);
          console.log(`   Final path: "${finalPath}"`);
        }

        // Determine level based on path depth
        // Path structure: Albums/English/Genre/Subgenre/Artist/Album/album.jpg
        // Note: finalPathParts now always includes Albums/English at the start
        let classifiedLevel = 'unknown';
        if (finalPathParts.length === 2) {
          // Language level: Albums/English/album.jpg
          classifiedLevel = 'language';
          imagesByLevel.language.push({
            file,
            path: finalPathParts.join('/') + '/',
            type: finalPathParts[0],
            language: finalPathParts[1],
          });
        } else if (finalPathParts.length === 3) {
          // Genre level: Albums/English/Rock/album.jpg
          classifiedLevel = 'genre';
          imagesByLevel.genre.push({
            file,
            path: finalPathParts.join('/') + '/',
            type: finalPathParts[0],
            language: finalPathParts[1],
            genre: finalPathParts[2],
          });
        } else if (finalPathParts.length === 4) {
          // Subgenre level: Albums/English/Rock/Classic Rock/album.jpg
          classifiedLevel = 'subgenre';
          imagesByLevel.subgenre.push({
            file,
            path: finalPathParts.join('/') + '/',
            type: finalPathParts[0],
            language: finalPathParts[1],
            genre: finalPathParts[2],
            subgenre: finalPathParts[3],
          });
        } else if (finalPathParts.length === 5) {
          // Artist level: Albums/English/Rock/Classic Rock/Beatles/album.jpg
          classifiedLevel = 'artist';
          imagesByLevel.artist.push({
            file,
            path: finalPathParts.join('/') + '/',
            type: finalPathParts[0],
            language: finalPathParts[1],
            genre: finalPathParts[2],
            subgenre: finalPathParts[3],
            artist: finalPathParts[4],
          });
        } else if (finalPathParts.length >= 6) {
          // Album level: Albums/English/Rock/Classic Rock/Beatles/Abbey Road/album.jpg
          classifiedLevel = 'album';
          imagesByLevel.album.push({
            file,
            path: finalPathParts.join('/') + '/',
            type: finalPathParts[0],
            language: finalPathParts[1],
            genre: finalPathParts[2],
            subgenre: finalPathParts[3],
            artist: finalPathParts[4],
            album: finalPathParts[5],
          });
        }

        // Debug: Log classification for files we're tracking
        if (fileIndex < 10 || (fileIndex < 50 && fileIndex % 10 === 0)) {
          console.log(
            `   → Classified as: ${classifiedLevel} level (pathParts.length: ${finalPathParts.length})`
          );
        }
      }

      if (skippedCount > 0) {
        console.log(
          `\n⚠️ Skipped ${skippedCount} files (not in Albums/English)`
        );
        if (skippedPaths.length > 0) {
          console.log(
            `   Sample skipped paths: ${skippedPaths.slice(0, 3).join(', ')}`
          );
        }
      }

      console.log(`\n📊 Library-wide scan results:`);
      console.log(`   - Type level: ${imagesByLevel.type.length}`);
      console.log(`   - Language level: ${imagesByLevel.language.length}`);
      if (imagesByLevel.language.length > 0) {
        console.log(
          `     Examples: ${imagesByLevel.language
            .slice(0, 3)
            .map((img) => img.path)
            .join(', ')}`
        );
      }
      console.log(`   - Genre level: ${imagesByLevel.genre.length}`);
      if (imagesByLevel.genre.length > 0) {
        console.log(
          `     Examples: ${imagesByLevel.genre
            .slice(0, 5)
            .map((img) => img.path)
            .join(', ')}`
        );
      }
      console.log(`   - Subgenre level: ${imagesByLevel.subgenre.length}`);
      if (imagesByLevel.subgenre.length > 0) {
        console.log(
          `     Examples: ${imagesByLevel.subgenre
            .slice(0, 3)
            .map((img) => img.path)
            .join(', ')}`
        );
      }
      console.log(`   - Artist level: ${imagesByLevel.artist.length}`);
      if (imagesByLevel.artist.length > 0) {
        console.log(
          `     Examples: ${imagesByLevel.artist
            .slice(0, 3)
            .map((img) => img.path)
            .join(', ')}`
        );
      }
      console.log(`   - Album level: ${imagesByLevel.album.length}`);
      if (imagesByLevel.album.length > 0) {
        console.log(
          `     Examples: ${imagesByLevel.album
            .slice(0, 3)
            .map((img) => img.path)
            .join(', ')}`
        );
      }

      const totalImages = Object.values(imagesByLevel).reduce(
        (sum, arr) => sum + arr.length,
        0
      );

      if (totalImages === 0) {
        alert(
          'No album.jpg files found at any level!\n\nMake sure you selected the root library folder (e.g., D:\\Jukebox\\Albums\\)'
        );
        setUploading(false);
        return;
      }

      const confirmMessage =
        `Ready to upload ${totalImages} cover image file(s) from Albums/English:\n` +
        `  - ${imagesByLevel.language.length} language level\n` +
        `  - ${imagesByLevel.genre.length} genre level\n` +
        `  - ${imagesByLevel.subgenre.length} subgenre level\n` +
        `  - ${imagesByLevel.artist.length} artist level\n` +
        `  - ${imagesByLevel.album.length} album level\n\n` +
        `Note: The file picker may show ${files.length} total files (including MP3s),\n` +
        `but only ${totalImages} cover image(s) will be uploaded.\n\n` +
        `This will upload ALL album.jpg/cover.jpg/folder.jpg files found in Albums/English.\n` +
        `Existing files will be overwritten.\n\n` +
        `Proceed with upload?`;

      if (!window.confirm(confirmMessage)) {
        setUploading(false);
        return;
      }

      setUploadStatus(`Uploading ${totalImages} images from entire library...`);

      // Upload all images by level
      const uploadLevel = async (levelName, images) => {
        for (const { file, path } of images) {
          const fileName = file.name;
          const storagePath = `${path}${fileName}`;

          setUploadStatus(`Uploading ${levelName} image: ${storagePath}`);

          try {
            const storageRef = ref(storage, storagePath);
            const uploadTask = uploadBytesResumable(storageRef, file);

            await new Promise((resolve, reject) => {
              uploadTask.on(
                'state_changed',
                (snapshot) => {
                  const progress =
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                  setUploadProgress((prev) => ({
                    ...prev,
                    [`${levelName}_${storagePath}`]: {
                      current: 1,
                      total: 1,
                      fileName: fileName,
                      progress: progress,
                    },
                  }));
                },
                (error) => reject(error),
                () => resolve()
              );
            });

            console.log(`   ✅ Uploaded ${levelName} image: ${storagePath}`);
            uploadedCount++;
          } catch (error) {
            console.error(
              `Error uploading ${levelName} image ${storagePath}:`,
              error
            );
            errorCount++;
          }
        }
      };

      // Upload in order: type -> language -> genre -> subgenre -> artist -> album
      await uploadLevel('type', imagesByLevel.type);
      await uploadLevel('language', imagesByLevel.language);
      await uploadLevel('genre', imagesByLevel.genre);
      await uploadLevel('subgenre', imagesByLevel.subgenre);
      await uploadLevel('artist', imagesByLevel.artist);
      await uploadLevel('album', imagesByLevel.album);

      setUploadStatus(
        `✅ Upload complete! ${uploadedCount} images uploaded from entire library. ${
          errorCount > 0 ? `${errorCount} errors.` : ''
        }`
      );
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus(`❌ Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
      setTimeout(() => {
        setShowUploadModal(false);
        setUploadProgress({});
      }, 3000);
    }
  };

  // Upload only cover images for selected albums OR auto-detect at all levels
  const handleCoverImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadStatus('Starting cover image upload...');
    setUploadProgress({});

    const selected = unavailableAlbums.filter((a) => selectedAlbums.has(a.id));
    const hasSelectedAlbums = selected.length > 0;
    let uploadedCount = 0;
    let errorCount = 0;

    try {
      // Filter to only image files
      const allImageFiles = files.filter((f) => {
        const fileName = f.name.toLowerCase();
        return (
          fileName.endsWith('.jpg') ||
          fileName.endsWith('.jpeg') ||
          fileName.endsWith('.png') ||
          fileName.endsWith('.webp')
        );
      });

      if (allImageFiles.length === 0) {
        alert(
          'No image files found! Please select a folder containing cover images (album.jpg, cover.jpg, etc.)'
        );
        setUploading(false);
        return;
      }

      console.log(
        `📁 Found ${allImageFiles.length} image files in selected folder`
      );

      // If no albums selected, auto-detect images at all levels
      if (!hasSelectedAlbums) {
        console.log(
          `🔍 Auto-detecting images at all levels (no albums selected)...`
        );

        // Filter to only album.jpg files (the standard name)
        const albumJpgFiles = allImageFiles.filter((f) => {
          const fileName = f.name.toLowerCase();
          return (
            fileName === 'album.jpg' ||
            fileName === 'cover.jpg' ||
            fileName === 'folder.jpg'
          );
        });

        console.log(
          `📷 Found ${albumJpgFiles.length} album.jpg/cover.jpg/folder.jpg files`
        );

        // Categorize images by level based on path structure
        const imagesByLevel = {
          subgenre: [],
          artist: [],
          album: [],
        };

        for (const file of albumJpgFiles) {
          const webkitPath = (file.webkitRelativePath || file.name).replace(
            /\\/g,
            '/'
          );
          const pathParts = webkitPath.split('/').filter((p) => p);

          // Determine level based on path structure:
          // Albums/English/Genre/Subgenre/album.jpg = subgenre level (4 parts)
          // Albums/English/Genre/Subgenre/Artist/album.jpg = artist level (5 parts)
          // Albums/English/Genre/Subgenre/Artist/Album/album.jpg = album level (6+ parts)

          if (pathParts.length === 4 && pathParts[0] === 'Albums') {
            // Subgenre level: Albums/Language/Genre/Subgenre/album.jpg
            imagesByLevel.subgenre.push({
              file,
              path: pathParts.slice(0, 4).join('/') + '/',
              language: pathParts[1],
              genre: pathParts[2],
              subgenre: pathParts[3],
            });
          } else if (pathParts.length === 5 && pathParts[0] === 'Albums') {
            // Artist level: Albums/Language/Genre/Subgenre/Artist/album.jpg
            imagesByLevel.artist.push({
              file,
              path: pathParts.slice(0, 5).join('/') + '/',
              language: pathParts[1],
              genre: pathParts[2],
              subgenre: pathParts[3],
              artist: pathParts[4],
            });
          } else if (pathParts.length >= 6 && pathParts[0] === 'Albums') {
            // Album level: Albums/Language/Genre/Subgenre/Artist/Album/album.jpg
            imagesByLevel.album.push({
              file,
              path: pathParts.slice(0, -1).join('/') + '/',
              language: pathParts[1],
              genre: pathParts[2],
              subgenre: pathParts[3],
              artist: pathParts[4],
              album: pathParts[5],
            });
          }
        }

        console.log(`📊 Auto-detected images:`);
        console.log(`   - Subgenre level: ${imagesByLevel.subgenre.length}`);
        console.log(`   - Artist level: ${imagesByLevel.artist.length}`);
        console.log(`   - Album level: ${imagesByLevel.album.length}`);

        const totalAutoDetected =
          imagesByLevel.subgenre.length +
          imagesByLevel.artist.length +
          imagesByLevel.album.length;

        if (totalAutoDetected === 0) {
          alert(
            'No album.jpg files found at subgenre, artist, or album levels!\n\nMake sure you selected a folder containing album.jpg files.'
          );
          setUploading(false);
          return;
        }

        const confirmMessage =
          `Ready to upload ${totalAutoDetected} image(s):\n` +
          `  - ${imagesByLevel.subgenre.length} subgenre image(s)\n` +
          `  - ${imagesByLevel.artist.length} artist image(s)\n` +
          `  - ${imagesByLevel.album.length} album image(s)\n\n` +
          `Proceed with upload?`;

        if (!window.confirm(confirmMessage)) {
          setUploading(false);
          return;
        }

        setUploadStatus(
          `Uploading ${totalAutoDetected} images at all levels...`
        );

        // Upload subgenre-level images
        for (const { file, path } of imagesByLevel.subgenre) {
          const fileName = file.name;
          const storagePath = `${path}${fileName}`;

          try {
            const storageRef = ref(storage, storagePath);
            const uploadTask = uploadBytesResumable(storageRef, file);

            await new Promise((resolve, reject) => {
              uploadTask.on(
                'state_changed',
                (snapshot) => {
                  const progress =
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                  setUploadProgress((prev) => ({
                    ...prev,
                    [`subgenre_${path}`]: {
                      current: 1,
                      total: 1,
                      fileName: fileName,
                      progress: progress,
                    },
                  }));
                },
                (error) => reject(error),
                () => resolve()
              );
            });

            console.log(`   ✅ Uploaded subgenre image: ${storagePath}`);
            uploadedCount++;
          } catch (error) {
            console.error(`Error uploading subgenre image:`, error);
            errorCount++;
          }
        }

        // Upload artist-level images
        for (const { file, path } of imagesByLevel.artist) {
          const fileName = file.name;
          const storagePath = `${path}${fileName}`;

          try {
            const storageRef = ref(storage, storagePath);
            const uploadTask = uploadBytesResumable(storageRef, file);

            await new Promise((resolve, reject) => {
              uploadTask.on(
                'state_changed',
                (snapshot) => {
                  const progress =
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                  setUploadProgress((prev) => ({
                    ...prev,
                    [`artist_${path}`]: {
                      current: 1,
                      total: 1,
                      fileName: fileName,
                      progress: progress,
                    },
                  }));
                },
                (error) => reject(error),
                () => resolve()
              );
            });

            console.log(`   ✅ Uploaded artist image: ${storagePath}`);
            uploadedCount++;
          } catch (error) {
            console.error(`Error uploading artist image:`, error);
            errorCount++;
          }
        }

        // Upload album-level images
        for (const { file, path } of imagesByLevel.album) {
          const fileName = file.name;
          const storagePath = `${path}${fileName}`;

          try {
            const storageRef = ref(storage, storagePath);
            const uploadTask = uploadBytesResumable(storageRef, file);

            await new Promise((resolve, reject) => {
              uploadTask.on(
                'state_changed',
                (snapshot) => {
                  const progress =
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                  setUploadProgress((prev) => ({
                    ...prev,
                    [`album_${path}`]: {
                      current: 1,
                      total: 1,
                      fileName: fileName,
                      progress: progress,
                    },
                  }));
                },
                (error) => reject(error),
                () => resolve()
              );
            });

            console.log(`   ✅ Uploaded album image: ${storagePath}`);
            uploadedCount++;
          } catch (error) {
            console.error(`Error uploading album image:`, error);
            errorCount++;
          }
        }

        setUploadStatus(
          `✅ Upload complete! ${uploadedCount} images uploaded. ${
            errorCount > 0 ? `${errorCount} errors.` : ''
          }`
        );

        setUploading(false);
        setTimeout(() => {
          setShowUploadModal(false);
          setUploadProgress({});
        }, 3000);
        return;
      }

      // Original logic: match images to selected albums
      console.log(`🎯 Selected ${selected.length} albums to match`);

      // Build list of expected album paths to filter images
      const expectedPaths = new Set();
      for (const album of selected) {
        if (album.type === 'Singles') {
          expectedPaths.add(
            `Singles/${album.language}/Various/Variety/Mix/${album.album}/`
          );
        } else {
          expectedPaths.add(
            `${album.type}/${album.language}/${album.genre}/${album.subgenre}/${album.artist}/${album.album}/`
          );
          // Also add path without genre (for physical structure)
          expectedPaths.add(
            `${album.type}/${album.language}/${album.subgenre}/${album.artist}/${album.album}/`
          );
        }
      }

      // Log what we're looking for
      console.log(`\n🔍 Expected album paths (${expectedPaths.size}):`);
      Array.from(expectedPaths)
        .slice(0, 10)
        .forEach((path) => {
          console.log(`   - ${path}`);
        });

      // Log sample of actual file paths
      console.log(`\n📁 Sample of actual file paths (first 5):`);
      allImageFiles.slice(0, 5).forEach((f) => {
        const path = (f.webkitRelativePath || f.name).replace(/\\/g, '/');
        console.log(`   - ${path}`);
      });

      // Filter images to only those in expected album paths
      // More flexible: check if path contains any album name from selected albums
      const albumNames = new Set(selected.map((a) => a.album));
      const imageFiles = allImageFiles.filter((f) => {
        const webkitPath = (f.webkitRelativePath || f.name).replace(/\\/g, '/');

        // Strategy 1: Check if path matches any expected full path
        for (const expectedPath of expectedPaths) {
          if (webkitPath.includes(expectedPath)) {
            return true;
          }
        }

        // Strategy 2: Check if path contains any album name (more flexible)
        // This handles cases where user selected a parent folder
        for (const albumName of albumNames) {
          // Check if path contains the album folder name
          const albumFolderPattern = new RegExp(
            `[/\\\\]${albumName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[/\\\\]`
          );
          if (albumFolderPattern.test(webkitPath)) {
            // Also verify it's in the right artist/genre area
            const hasArtist = selected.some(
              (a) => a.album === albumName && webkitPath.includes(a.artist)
            );
            const hasGenre = selected.some(
              (a) =>
                a.album === albumName &&
                (webkitPath.includes(a.genre) ||
                  webkitPath.includes(a.subgenre))
            );
            if (hasArtist || hasGenre) {
              return true;
            }
          }
        }

        return false;
      });

      console.log(
        `✅ Filtered to ${imageFiles.length} images in expected album paths (out of ${allImageFiles.length} total images)`
      );

      if (imageFiles.length === 0) {
        // Show more helpful error message
        const samplePaths = allImageFiles
          .slice(0, 3)
          .map((f) => (f.webkitRelativePath || f.name).replace(/\\/g, '/'));
        alert(
          `No images found in the expected album paths!\n\n` +
            `Expected paths (sample):\n${Array.from(expectedPaths)
              .slice(0, 3)
              .join('\n')}\n\n` +
            `Actual file paths (sample):\n${samplePaths.join('\n')}\n\n` +
            `Please check:\n` +
            `1. Did you select the correct folder? (Should be: D:\\Jukebox\\Albums\\English\\R and B\\Pop\\Michael Jackson\\)\n` +
            `2. Do the cover images exist in the album folders?\n` +
            `3. Check the browser console for more details.`
        );
        setUploading(false);
        return;
      }

      // Determine base path from first file
      let basePath = '';
      if (imageFiles.length > 0) {
        const firstPath = (
          imageFiles[0].webkitRelativePath || imageFiles[0].name
        ).replace(/\\/g, '/');
        const pathParts = firstPath.split('/');
        if (pathParts.length > 1) {
          basePath = pathParts.slice(0, -2).join('/') + '/';
        }
        console.log(`📍 Detected base path: "${basePath}"`);
      }

      // Match images to albums AND higher-level folders (subgenre, artist)
      const imagesByAlbum = {};
      const imagesBySubgenre = {}; // Track subgenre-level images
      const imagesByArtist = {}; // Track artist-level images
      const matchedImagePaths = new Set(); // Track which images we've matched

      for (const album of selected) {
        console.log(
          `\n🔍 Looking for cover image: ${album.artist} - ${album.album}`
        );
        let albumPath;
        if (album.type === 'Singles') {
          albumPath = `Singles/${album.language}/Various/Variety/Mix/${album.album}/`;
        } else {
          albumPath = `${album.type}/${album.language}/${album.genre}/${album.subgenre}/${album.artist}/${album.album}/`;
        }

        const albumImageFiles = imageFiles.filter((file) => {
          const webkitPath = file.webkitRelativePath || file.name;
          const normalizedPath = webkitPath.replace(/\\/g, '/');
          const fileName = file.name;

          // STRICT MATCHING: Image must be directly in the album folder
          // Path should be: .../AlbumName/image.jpg (where AlbumName matches exactly)

          if (album.type === 'Singles') {
            // For Singles: Singles/Language/Various/Variety/Mix/AlbumName/image.jpg
            const expectedPath = `Singles/${album.language}/Various/Variety/Mix/${album.album}/${fileName}`;
            if (normalizedPath === expectedPath) return true;

            // Also try with basePath if user selected a subfolder
            if (basePath && normalizedPath.startsWith(basePath)) {
              const relativePath = normalizedPath.substring(basePath.length);
              // Should be: AlbumName/image.jpg
              if (relativePath.startsWith(`${album.album}/${fileName}`))
                return true;
            }
            return false;
          } else {
            // For Albums: Albums/Language/Genre/Subgenre/Artist/AlbumName/image.jpg
            const escapedAlbum = album.album.replace(
              /[.*+?^${}()|[\]\\]/g,
              '\\$&'
            );

            // Strategy 1: Full path match
            const expectedPath = `${album.type}/${album.language}/${album.genre}/${album.subgenre}/${album.artist}/${album.album}/${fileName}`;
            if (normalizedPath === expectedPath) return true;

            // Strategy 2: Without genre (physical path structure)
            const expectedPathNoGenre = `${album.type}/${album.language}/${album.subgenre}/${album.artist}/${album.album}/${fileName}`;
            if (normalizedPath === expectedPathNoGenre) return true;

            // Strategy 3: With basePath - image must be in the album folder
            if (basePath && normalizedPath.startsWith(basePath)) {
              const relativePath = normalizedPath.substring(basePath.length);
              // Image should be directly in album folder: AlbumName/image.jpg
              // OR: Artist/AlbumName/image.jpg
              const exactAlbumMatch = new RegExp(
                `^${escapedAlbum}/${fileName}$|^${album.artist.replace(
                  /[.*+?^${}()|[\]\\]/g,
                  '\\$&'
                )}/${escapedAlbum}/${fileName}$`
              );
              if (exactAlbumMatch.test(relativePath)) return true;
            }

            // Strategy 4: Regex match for exact album folder
            // Path must end with: /AlbumName/image.jpg
            const exactAlbumRegex = new RegExp(`/${escapedAlbum}/${fileName}$`);
            if (exactAlbumRegex.test(normalizedPath)) {
              // Verify it's the right artist/genre structure
              if (
                normalizedPath.includes(`/${album.artist}/${album.album}/`) ||
                normalizedPath.includes(`/${album.subgenre}/${album.album}/`)
              ) {
                return true;
              }
            }

            return false;
          }
        });

        if (albumImageFiles.length > 0) {
          // Filter out images we've already matched to another album
          const unmatchedImages = albumImageFiles.filter((img) => {
            const imgPath = (img.webkitRelativePath || img.name).replace(
              /\\/g,
              '/'
            );
            return !matchedImagePaths.has(imgPath);
          });

          if (unmatchedImages.length > 0) {
            // Prefer album.jpg, cover.jpg, folder.jpg, then any image
            const sortedImages = unmatchedImages.sort((a, b) => {
              const aName = a.name.toLowerCase();
              const bName = b.name.toLowerCase();
              const priority = ['album.jpg', 'cover.jpg', 'folder.jpg'];
              const aIndex = priority.findIndex((p) => aName.includes(p));
              const bIndex = priority.findIndex((p) => bName.includes(p));
              if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
              if (aIndex !== -1) return -1;
              if (bIndex !== -1) return 1;
              return 0;
            });

            const selectedImage = sortedImages[0];
            const imgPath = (
              selectedImage.webkitRelativePath || selectedImage.name
            ).replace(/\\/g, '/');
            matchedImagePaths.add(imgPath);

            imagesByAlbum[album.id] = {
              album,
              file: selectedImage,
              path: albumPath,
            };
            console.log(`   ✅ Found cover image: ${selectedImage.name}`);
            console.log(`   📂 Path: ${imgPath}`);
          } else {
            console.log(
              `   ⚠️ All matching images already assigned to other albums`
            );
          }
        } else {
          console.log(`   ❌ No cover image found for "${album.album}"`);
        }
      }

      // Also detect subgenre-level and artist-level images
      const uniqueSubgenres = new Set();
      const uniqueArtists = new Set();
      selected.forEach((album) => {
        if (album.type === 'Albums') {
          uniqueSubgenres.add(
            `${album.language}|${album.genre}|${album.subgenre}`
          );
          uniqueArtists.add(
            `${album.language}|${album.genre}|${album.subgenre}|${album.artist}`
          );
        }
      });

      console.log(`\n🔍 Looking for subgenre-level images...`);
      for (const subgenreKey of uniqueSubgenres) {
        const [language, genre, subgenre] = subgenreKey.split('|');
        const subgenrePath = `Albums/${language}/${genre}/${subgenre}/`;
        const subgenreImageFiles = imageFiles.filter((file) => {
          const webkitPath = (file.webkitRelativePath || file.name).replace(
            /\\/g,
            '/'
          );
          const fileName = file.name.toLowerCase();
          // Check if image is directly in subgenre folder (path should end with: .../Subgenre/album.jpg)
          // Not in an artist or album subfolder
          const expectedSubgenrePath = `${subgenrePath}${file.name}`;
          if (
            webkitPath === expectedSubgenrePath ||
            webkitPath.endsWith(`/${subgenre}/${file.name}`)
          ) {
            // Verify it's not in a deeper folder (artist or album)
            const afterSubgenre = webkitPath.substring(
              webkitPath.indexOf(subgenrePath) + subgenrePath.length
            );
            if (!afterSubgenre.includes('/')) {
              // Directly in subgenre folder
              return (
                fileName === 'album.jpg' ||
                fileName === 'cover.jpg' ||
                fileName === 'folder.jpg'
              );
            }
          }
          return false;
        });

        if (subgenreImageFiles.length > 0) {
          const img = subgenreImageFiles[0];
          const imgPath = (img.webkitRelativePath || img.name).replace(
            /\\/g,
            '/'
          );
          if (!matchedImagePaths.has(imgPath)) {
            imagesBySubgenre[subgenreKey] = { file: img, path: subgenrePath };
            matchedImagePaths.add(imgPath);
            console.log(
              `   ✅ Found subgenre image: ${subgenre} - ${img.name}`
            );
          }
        }
      }

      console.log(`\n🔍 Looking for artist-level images...`);
      for (const artistKey of uniqueArtists) {
        const [language, genre, subgenre, artist] = artistKey.split('|');
        const artistPath = `Albums/${language}/${genre}/${subgenre}/${artist}/`;
        const artistImageFiles = imageFiles.filter((file) => {
          const webkitPath = (file.webkitRelativePath || file.name).replace(
            /\\/g,
            '/'
          );
          const fileName = file.name.toLowerCase();
          // Check if image is directly in artist folder (not in an album subfolder)
          if (!webkitPath.includes(artistPath)) return false;
          const afterArtistPath = webkitPath.substring(
            webkitPath.indexOf(artistPath) + artistPath.length
          );
          // Should be just the filename, not in a subfolder
          if (afterArtistPath.includes('/')) return false;
          return (
            fileName === 'album.jpg' ||
            fileName === 'cover.jpg' ||
            fileName === 'folder.jpg'
          );
        });

        if (artistImageFiles.length > 0) {
          const img = artistImageFiles[0];
          const imgPath = (img.webkitRelativePath || img.name).replace(
            /\\/g,
            '/'
          );
          if (!matchedImagePaths.has(imgPath)) {
            imagesByArtist[artistKey] = { file: img, path: artistPath };
            matchedImagePaths.add(imgPath);
            console.log(`   ✅ Found artist image: ${artist} - ${img.name}`);
          }
        }
      }

      console.log(`\n📊 Matching Summary:`);
      console.log(
        `   - Total files selected by browser: ${files.length} (includes MP3s, images, etc.)`
      );
      console.log(`   - Image files found in folder: ${allImageFiles.length}`);
      console.log(
        `   - Image files in expected album paths: ${imageFiles.length}`
      );
      console.log(
        `   - Albums with matched images: ${Object.keys(imagesByAlbum).length}`
      );
      console.log(
        `   - Subgenres with matched images: ${
          Object.keys(imagesBySubgenre).length
        }`
      );
      console.log(
        `   - Artists with matched images: ${
          Object.keys(imagesByArtist).length
        }`
      );
      console.log(`   - Albums selected: ${selected.length}`);

      const totalImages =
        Object.keys(imagesByAlbum).length +
        Object.keys(imagesBySubgenre).length +
        Object.keys(imagesByArtist).length;

      if (totalImages === 0) {
        alert('No cover images matched! Check console for details.');
        setUploading(false);
        return;
      }

      if (Object.keys(imagesByAlbum).length < selected.length) {
        const unmatched = selected.length - Object.keys(imagesByAlbum).length;
        console.warn(
          `⚠️ Warning: ${unmatched} album(s) did not have matching cover images`
        );
      }

      // Show confirmation with exact count - ONLY image files, not MP3s
      const albumCount = Object.keys(imagesByAlbum).length;
      const subgenreCount = Object.keys(imagesBySubgenre).length;
      const artistCount = Object.keys(imagesByArtist).length;
      const confirmMessage =
        `Ready to upload ${totalImages} image(s):\n` +
        `  - ${albumCount} album cover image(s)\n` +
        `  - ${subgenreCount} subgenre image(s)\n` +
        `  - ${artistCount} artist image(s)\n\n` +
        `Note: The file picker may show ${files.length} total files (including MP3s), but only ${totalImages} image(s) will be uploaded.\n\n` +
        `Proceed with upload?`;

      if (!window.confirm(confirmMessage)) {
        setUploading(false);
        return;
      }

      setUploadStatus(
        `Uploading ${totalImages} images (album, subgenre, and artist levels)...`
      );

      // Upload cover images
      for (const [albumId, { album, file, path }] of Object.entries(
        imagesByAlbum
      )) {
        const fileName = file.name;
        const storagePath = `${path}${fileName}`;

        setUploadStatus(`Uploading cover: ${album.artist} - ${album.album}`);

        try {
          const storageRef = ref(storage, storagePath);
          const uploadTask = uploadBytesResumable(storageRef, file);

          await new Promise((resolve, reject) => {
            uploadTask.on(
              'state_changed',
              (snapshot) => {
                const progress =
                  (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress((prev) => ({
                  ...prev,
                  [albumId]: {
                    current: 1,
                    total: 1,
                    fileName: fileName,
                    progress: progress,
                  },
                }));
              },
              (error) => {
                console.error('Upload error:', error);
                reject(error);
              },
              () => resolve()
            );
          });

          // Update Firestore with coverUrl
          const coverUrlPath =
            album.type === 'Singles'
              ? `/${album.language}/Various/Variety/Mix/${album.album}/${fileName}`
              : `/${album.language}/${album.genre}/${album.subgenre}/${album.artist}/${album.album}/${fileName}`;

          await updateDoc(doc(db, 'albums-flat', albumId), {
            coverUrl: coverUrlPath,
            syncedAt: new Date().toISOString(),
          });

          console.log(`   ✅ Uploaded and updated: ${fileName}`);
          uploadedCount++;
        } catch (error) {
          console.error(`Error uploading ${fileName}:`, error);
          errorCount++;
        }
      }

      // Upload subgenre-level images
      for (const [subgenreKey, { file, path }] of Object.entries(
        imagesBySubgenre
      )) {
        const fileName = file.name;
        const storagePath = `${path}${fileName}`;
        const [, , subgenre] = subgenreKey.split('|');

        setUploadStatus(`Uploading subgenre image: ${subgenre}`);

        try {
          const storageRef = ref(storage, storagePath);
          const uploadTask = uploadBytesResumable(storageRef, file);

          await new Promise((resolve, reject) => {
            uploadTask.on(
              'state_changed',
              (snapshot) => {
                const progress =
                  (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress((prev) => ({
                  ...prev,
                  [`subgenre_${subgenreKey}`]: {
                    current: 1,
                    total: 1,
                    fileName: fileName,
                    progress: progress,
                  },
                }));
              },
              (error) => {
                console.error('Upload error:', error);
                reject(error);
              },
              () => resolve()
            );
          });

          console.log(
            `   ✅ Uploaded subgenre image: ${fileName} to ${storagePath}`
          );
          uploadedCount++;
        } catch (error) {
          console.error(`Error uploading subgenre image ${fileName}:`, error);
          errorCount++;
        }
      }

      // Upload artist-level images
      for (const [artistKey, { file, path }] of Object.entries(
        imagesByArtist
      )) {
        const fileName = file.name;
        const storagePath = `${path}${fileName}`;
        const [, , , artist] = artistKey.split('|');

        setUploadStatus(`Uploading artist image: ${artist}`);

        try {
          const storageRef = ref(storage, storagePath);
          const uploadTask = uploadBytesResumable(storageRef, file);

          await new Promise((resolve, reject) => {
            uploadTask.on(
              'state_changed',
              (snapshot) => {
                const progress =
                  (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress((prev) => ({
                  ...prev,
                  [`artist_${artistKey}`]: {
                    current: 1,
                    total: 1,
                    fileName: fileName,
                    progress: progress,
                  },
                }));
              },
              (error) => {
                console.error('Upload error:', error);
                reject(error);
              },
              () => resolve()
            );
          });

          console.log(
            `   ✅ Uploaded artist image: ${fileName} to ${storagePath}`
          );
          uploadedCount++;
        } catch (error) {
          console.error(`Error uploading artist image ${fileName}:`, error);
          errorCount++;
        }
      }

      setUploadStatus(
        `✅ Upload complete! ${uploadedCount} images uploaded (${albumCount} albums, ${subgenreCount} subgenres, ${artistCount} artists). ${
          errorCount > 0 ? `${errorCount} errors.` : ''
        }`
      );
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus(`❌ Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
      setTimeout(() => {
        setShowUploadModal(false);
        setUploadProgress({});
      }, 3000);
    }
  };

  // Handle folder selection and upload
  const handleFolderUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadStatus('Starting upload...');
    setUploadProgress({});

    const selected = unavailableAlbums.filter((a) => selectedAlbums.has(a.id));
    let uploadedCount = 0;
    let errorCount = 0;

    try {
      // Group files by album path
      const filesByAlbum = {};

      // Filter to MP3 files AND cover images
      const mp3Files = files.filter((f) => {
        const fileName = f.name.toLowerCase();
        return (
          fileName.endsWith('.mp3') ||
          fileName.endsWith('.m4a') ||
          fileName.endsWith('.flac') ||
          fileName.endsWith('.wav')
        );
      });

      // Also get cover image files (album.jpg, cover.jpg, folder.jpg, etc.)
      const imageFiles = files.filter((f) => {
        const fileName = f.name.toLowerCase();
        return (
          fileName.endsWith('.jpg') ||
          fileName.endsWith('.jpeg') ||
          fileName.endsWith('.png') ||
          fileName.endsWith('.webp')
        );
      });

      console.log(`📁 Files selected: ${files.length} total`);
      console.log(`   - Audio files: ${mp3Files.length}`);
      console.log(`   - Image files: ${imageFiles.length}`);

      if (mp3Files.length === 0) {
        console.error('❌ No MP3 files found in selected folder!');
        console.log(`   Total files selected: ${files.length}`);
        console.log('   File types found:', [
          ...new Set(
            files.map((f) => {
              const ext =
                f.name.split('.').pop()?.toLowerCase() || 'no extension';
              return ext;
            })
          ),
        ]);
        setUploadStatus(
          '❌ No MP3 files found in selected folder. Please select a folder containing .mp3 files.'
        );
        setUploading(false);
        return;
      }

      console.log(
        `📁 Found ${mp3Files.length} MP3 files out of ${files.length} total files`
      );

      // Debug: Show first few file paths
      console.log('📁 Sample file paths from browser:');
      mp3Files.slice(0, 10).forEach((f) => {
        const path = (f.webkitRelativePath || f.name).replace(/\\/g, '/');
        console.log(`  ${path}`);
      });
      console.log('');

      // Check if user selected a specific folder (all files are just filenames)
      const allFilesAreJustFilenames = mp3Files.every((f) => {
        const path = (f.webkitRelativePath || f.name).replace(/\\/g, '/');
        return !path.includes('/');
      });

      if (allFilesAreJustFilenames && selected.length === 1) {
        console.log(
          '📁 User selected a specific album folder. Matching all files to the selected album.'
        );
      }

      // Determine the base path from the first file (to understand where user selected)
      let basePath = '';
      if (mp3Files.length > 0) {
        const firstPath = (
          mp3Files[0].webkitRelativePath || mp3Files[0].name
        ).replace(/\\/g, '/');
        // Extract the base path (everything before the album folder)
        // For example: "Albums/English/Rock/Classic Rock/Various/Rockin USA Los Angeles/TRACK01.MP3"
        // Base would be "Albums/English/Rock/Classic Rock/"
        const pathParts = firstPath.split('/');
        if (pathParts.length > 1) {
          // Remove the filename and album folder to get base
          basePath = pathParts.slice(0, -2).join('/') + '/';
        }
        console.log(`📍 Detected base path from files: "${basePath}"`);
      }

      for (const album of selected) {
        // For Singles, use the hardcoded Various/Variety/Mix structure
        let albumPath;
        if (album.type === 'Singles') {
          // Singles: Singles/Language/Various/Variety/Mix/[Artist]/
          albumPath = `Singles/${album.language}/Various/Variety/Mix/${album.album}/`;
        } else {
          // Albums: Albums/Language/Genre/Subgenre/Artist/Album/
          albumPath = `${album.type}/${album.language}/${album.genre}/${album.subgenre}/${album.artist}/${album.album}/`;
        }

        console.log(
          `\n🔍 Matching files for album: "${album.artist}" - "${album.album}"`
        );
        console.log(`   Expected path: ${albumPath}`);
        console.log(
          `   Album type: ${album.type}, Language: ${album.language}, Genre: ${album.genre}, Subgenre: ${album.subgenre}`
        );

        let fileIndex = 0;
        const albumFiles = mp3Files.filter((file) => {
          const webkitPath = file.webkitRelativePath || file.name;
          const normalizedPath = webkitPath.replace(/\\/g, '/');
          const isFirstFile = fileIndex === 0;
          fileIndex++;

          // Debug first file for each album
          if (isFirstFile) {
            console.log(`   🔍 Checking file: "${normalizedPath}"`);
          }

          // For Singles, match against the full expected path structure
          if (album.type === 'Singles') {
            // Match: Singles/Language/Various/Variety/Mix/[AlbumFolder]/
            const expectedPathPart = `Singles/${album.language}/Various/Variety/Mix/${album.album}/`;

            // Debug first album only
            if (Object.keys(filesByAlbum).length === 0) {
              console.log(`🔍 Looking for album: ${album.album}`);
              console.log(`   Expected path part: ${expectedPathPart}`);
              console.log(`   Sample file: ${normalizedPath}`);
              console.log(
                `   Match: ${normalizedPath.includes(expectedPathPart)}`
              );
            }

            return normalizedPath.includes(expectedPathPart);
          } else {
            // For Albums, try multiple matching strategies

            // Check if this is a compilation album (artist="Various")
            // For compilations, we skip artist checks and match by album name only
            const isCompilation =
              album.artist && album.artist.toLowerCase() === 'various';

            // Strategy 1: Match by full path structure (if user selected root folder)
            // Try with artist folder first
            const expectedPathPart = `${album.type}/${album.language}/${album.genre}/${album.subgenre}/${album.artist}/${album.album}/`;
            if (normalizedPath.includes(expectedPathPart)) {
              console.log(
                `   ✅ Matched "${album.album}" using Strategy 1 (full path with artist)`
              );
              return true;
            }

            // Strategy 1a: Try without artist folder (for cases like Decades where artist folder might not exist)
            const expectedPathPartNoArtist = `${album.type}/${album.language}/${album.genre}/${album.subgenre}/${album.album}/`;
            // Use exact regex matching to ensure we match the full album name
            const escapedAlbumForStrategy1a = album.album.replace(
              /[.*+?^${}()|[\]\\]/g,
              '\\$&'
            );
            const exactAlbumRegex1a = new RegExp(
              `(^|[/\\\\])${album.type}/${album.language}/${album.genre}/${album.subgenre}/${escapedAlbumForStrategy1a}([/\\\\]|$)`
            );
            if (exactAlbumRegex1a.test(normalizedPath)) {
              console.log(
                `   ✅ Matched "${album.album}" using Strategy 1a (path without artist folder)`
              );
              return true;
            }

            // Strategy 1c: Try without genre folder (for cases where physical path is simpler)
            // Physical: Albums/English/Decades/ vs Expected: Albums/English/Rock/Decades/
            // Match: Albums/English/Decades/THE BEST SIXTIES 1/
            const expectedPathPartNoGenre = `${album.type}/${album.language}/${album.subgenre}/${album.album}/`;
            const escapedAlbumForStrategy1c = album.album.replace(
              /[.*+?^${}()|[\]\\]/g,
              '\\$&'
            );
            const exactAlbumRegex1c = new RegExp(
              `(^|[/\\\\])${album.type}/${album.language}/${album.subgenre}/${escapedAlbumForStrategy1c}([/\\\\]|$)`
            );
            if (exactAlbumRegex1c.test(normalizedPath)) {
              console.log(
                `   ✅ Matched "${album.album}" using Strategy 1c (path without genre folder - matches physical structure)`
              );
              return true;
            }

            // Strategy 1d: Try without genre AND artist folder (simplest structure)
            // Physical: Albums/English/Decades/THE BEST SIXTIES 1/
            const expectedPathPartNoGenreNoArtist = `${album.type}/${album.language}/${album.subgenre}/${album.album}/`;
            // Already checked in 1c, but also try with artist if basePath suggests it
            if (
              basePath &&
              basePath.includes(
                `${album.type}/${album.language}/${album.subgenre}/`
              )
            ) {
              const exactAlbumRegex1d = new RegExp(
                `(^|[/\\\\])${album.type}/${album.language}/${album.subgenre}/${escapedAlbumForStrategy1c}([/\\\\]|$)`
              );
              if (exactAlbumRegex1d.test(normalizedPath)) {
                console.log(
                  `   ✅ Matched "${album.album}" using Strategy 1d (simplified path matching basePath)`
                );
                return true;
              }
            }

            // Strategy 1b: Match with base path (if user selected a subfolder)
            // For example, if user selected "Classic Rock", files might be "Various/Rockin USA Los Angeles/TRACK01.MP3"
            // We need to check if the path contains the artist/album structure
            // For compilation albums (artist="Various"), skip artist check and match by album name only
            if (basePath && normalizedPath.startsWith(basePath)) {
              // Remove base path and check if remaining path matches album structure
              const relativePath = normalizedPath.substring(basePath.length);

              // CRITICAL FIX: Match EXACT album folder name, not substring
              // Use regex to match album name as a complete folder (bounded by / or \ or start/end)
              const escapedAlbum = album.album.replace(
                /[.*+?^${}()|[\]\\]/g,
                '\\$&'
              );
              const exactAlbumRegex = new RegExp(
                `(^|[/\\\\])${escapedAlbum}([/\\\\]|$)`
              );
              const exactAlbumMatch = exactAlbumRegex.test(relativePath);

              // Check if path matches physical structure (may not have artist folder)
              // For compilations, skip artist check; otherwise check if artist is in path OR if basePath suggests no artist folder
              const basePathHasNoGenre =
                basePath.includes(
                  `${album.type}/${album.language}/${album.subgenre}/`
                ) && !basePath.includes(`/${album.genre}/`);
              const hasArtistMatch =
                isCompilation ||
                basePathHasNoGenre ||
                relativePath.includes(album.artist);

              if (hasArtistMatch && exactAlbumMatch) {
                console.log(
                  `   ✅ Matched "${album.album}" using Strategy 1b (base path + exact album folder match)`
                );
                return true;
              }

              // Also try without artist requirement if basePath suggests simpler structure
              if (basePathHasNoGenre && exactAlbumMatch) {
                console.log(
                  `   ✅ Matched "${album.album}" using Strategy 1b (base path without genre/artist - matches physical structure)`
                );
                return true;
              }
            }

            // Strategy 2b: Try normalized matching for special characters (fallback)
            // This handles cases where folder names have different apostrophe types or special chars
            const normalizeForMatching = (str) => {
              if (!str) return '';
              return str
                .toLowerCase()
                .replace(/[''`'']/g, '') // Remove all apostrophe/quote types (folder might not have them)
                .replace(/[:]/g, '') // Remove colons for matching
                .replace(/[-–—]/g, ' ') // Replace dashes with spaces (for word matching)
                .replace(/\s+/g, ' ') // Normalize whitespace
                .trim();
            };

            // Extract just the significant words (ignore common words like "the", "a", "of", etc.)
            const extractSignificantWords = (str) => {
              const normalized = normalizeForMatching(str);
              const words = normalized.split(/\s+/).filter((w) => w.length > 2); // Filter out very short words
              return words;
            };

            const normalizedPathForMatch = normalizeForMatching(normalizedPath);
            const normalizedArtist = normalizeForMatching(album.artist);
            const normalizedAlbum = normalizeForMatching(album.album);

            // Strategy 2: Match by artist and album name in path (if user selected a parent folder)
            // This handles cases like selecting "Classic Rock" folder which contains multiple albums
            // Use case-insensitive matching
            // For compilation albums (artist="Various"), skip artist check and match by album name only
            const hasArtistMatch =
              isCompilation ||
              normalizedPathForMatch.includes(normalizedArtist);

            // CRITICAL FIX: Match EXACT album folder name using regex (not substring)
            // Escape special regex characters in album name
            const escapedAlbum = normalizedAlbum.replace(
              /[.*+?^${}()|[\]\\]/g,
              '\\$&'
            );
            const exactAlbumRegex = new RegExp(
              `(^|[/\\\\])${escapedAlbum}([/\\\\]|$)`
            );
            const exactAlbumMatch = exactAlbumRegex.test(
              normalizedPathForMatch
            );

            if (hasArtistMatch && exactAlbumMatch) {
              console.log(
                `   ✅ Matched "${album.album}" using Strategy 2 (case-insensitive exact album folder match)`
              );
              return true;
            }

            // Extract words from album name and path for flexible matching
            const albumWords = extractSignificantWords(album.album);
            const pathWords = extractSignificantWords(normalizedPath);

            // Check if significant words from album name appear in path
            const matchingWords = albumWords.filter((word) =>
              pathWords.some(
                (pathWord) => pathWord.includes(word) || word.includes(pathWord)
              )
            );

            // If most significant words match, consider it a match
            const wordMatchRatio =
              albumWords.length > 0
                ? matchingWords.length / albumWords.length
                : 0;
            const hasGoodWordMatch = wordMatchRatio >= 0.6; // At least 60% of words match

            // Debug logging for the problematic album
            if (
              album.album &&
              (album.album.toLowerCase().includes('rockin') ||
                album.album.toLowerCase().includes('usa'))
            ) {
              console.log(`🔍 Debugging "${album.album}":`);
              console.log(`   Original album name: "${album.album}"`);
              console.log(`   Normalized album: "${normalizedAlbum}"`);
              console.log(`   Album words: [${albumWords.join(', ')}]`);
              console.log(`   File path: "${normalizedPath}"`);
              console.log(`   Normalized path: "${normalizedPathForMatch}"`);
              console.log(`   Path words: [${pathWords.join(', ')}]`);
              console.log(`   Matching words: [${matchingWords.join(', ')}]`);
              console.log(
                `   Word match ratio: ${(wordMatchRatio * 100).toFixed(0)}%`
              );
              console.log(`   Has good word match: ${hasGoodWordMatch}`);
            }

            // Try exact normalized match first
            // CRITICAL FIX: Use regex for exact folder name matching
            const escapedAlbumForExact = normalizedAlbum.replace(
              /[.*+?^${}()|[\]\\]/g,
              '\\$&'
            );
            const exactAlbumRegex2 = new RegExp(
              `(^|[/\\\\])${escapedAlbumForExact}([/\\\\]|$)`
            );
            const exactAlbumMatch2 = exactAlbumRegex2.test(
              normalizedPathForMatch
            );

            if (
              normalizedPathForMatch.includes(normalizedArtist) &&
              exactAlbumMatch2
            ) {
              console.log(
                `✅ Matched "${album.album}" using Strategy 2b (normalized exact folder match)`
              );
              return true;
            }

            // Try word-based matching (for cases where folder name is shorter/different)
            // For compilation albums (artist="Various"), skip artist check and match by album name only
            // Reuse hasArtistMatch from Strategy 2 above
            // CRITICAL FIX: Disable word-based matching when multiple albums are selected with similar names
            // This prevents "THE BEST SIXTIES" from matching all "THE BEST SIXTIES 1-7" albums
            if (hasGoodWordMatch && hasArtistMatch && selected.length === 1) {
              // Only use word-based matching when exactly one album is selected
              // Check if the folder structure suggests this is the right album
              // Look for the album folder name pattern in the path
              const albumFolderPattern =
                albumWords.length > 0 ? albumWords[0] : ''; // Use first significant word
              if (
                albumFolderPattern &&
                normalizedPathForMatch.includes(albumFolderPattern)
              ) {
                console.log(
                  `✅ Matched "${album.album}" using Strategy 2b (word-based ${
                    isCompilation ? 'album only' : 'artist/album'
                  } match)`
                );
                return true;
              }
            }

            // Strategy 3: If webkitPath is just a filename (user selected album folder directly),
            // and only one album is selected, match all MP3 files to that album
            const isJustFilename =
              !normalizedPath.includes('/') && !normalizedPath.includes('\\');
            if (isJustFilename && selected.length === 1) {
              console.log(
                `   ✅ Matched "${album.album}" using Strategy 3 (direct folder - just filename)`
              );
              return true;
            }

            // Strategy 4: Match by album folder name only (if path contains album folder)
            // CRITICAL FIX: Use regex for exact folder name matching (not substring)
            const escapedAlbumForStrategy4 = normalizedAlbum.replace(
              /[.*+?^${}()|[\]\\]/g,
              '\\$&'
            );
            const exactAlbumRegex4 = new RegExp(
              `(^|[/\\\\])${escapedAlbumForStrategy4}([/\\\\]|$)`
            );
            const albumNameInPath = exactAlbumRegex4.test(
              normalizedPathForMatch
            );

            if (albumNameInPath && selected.length === 1) {
              console.log(
                `   ✅ Matched "${album.album}" using Strategy 4 (exact album folder name match)`
              );
              return true;
            }

            // Strategy 5: Match by album name with word-based matching (when artist not in path)
            // This handles cases where user selected the album folder directly
            // and the path is just "AlbumName/TRACK01.MP3" without the full structure
            // CRITICAL FIX: Use regex for exact folder name matching
            if (hasGoodWordMatch && selected.length === 1) {
              const escapedAlbumForStrategy5 = normalizedAlbum.replace(
                /[.*+?^${}()|[\]\\]/g,
                '\\$&'
              );
              const exactAlbumRegex5 = new RegExp(
                `(^|[/\\\\])${escapedAlbumForStrategy5}([/\\\\]|$)`
              );
              const exactAlbumMatch5 = exactAlbumRegex5.test(
                normalizedPathForMatch
              );

              if (exactAlbumMatch5) {
                console.log(
                  `   ✅ Matched "${album.album}" using Strategy 5 (exact album folder match)`
                );
                return true;
              }
            }

            // Debug: Log why this file didn't match (only for first file of first album)
            if (isFirstFile && Object.keys(filesByAlbum).length === 0) {
              console.log(
                `   ❌ File "${normalizedPath}" did not match album "${album.album}"`
              );
              console.log(
                `      - Contains artist "${
                  album.artist
                }": ${normalizedPath.includes(album.artist)}`
              );
              console.log(
                `      - Contains album "${
                  album.album
                }": ${normalizedPath.includes(album.album)}`
              );
              console.log(`      - Is just filename: ${isJustFilename}`);
              console.log(`      - Album name in path: ${albumNameInPath}`);
              console.log(`      - Selected albums count: ${selected.length}`);
              console.log(`      - Has good word match: ${hasGoodWordMatch}`);
              console.log(
                `      - Path starts with album: ${normalizedPathForMatch.startsWith(
                  normalizedAlbum + '/'
                )}`
              );
            }

            return false;
          }
        });

        // Also find cover images for this album
        const albumImageFiles = imageFiles.filter((file) => {
          const webkitPath = file.webkitRelativePath || file.name;
          const normalizedPath = webkitPath.replace(/\\/g, '/');

          if (album.type === 'Singles') {
            const expectedPathPart = `Singles/${album.language}/Various/Variety/Mix/${album.album}/`;
            return normalizedPath.includes(expectedPathPart);
          } else {
            // Try multiple matching strategies for images (same as audio files)
            const isCompilation =
              album.artist && album.artist.toLowerCase() === 'various';

            // Strategy 1: Full path with artist
            const expectedPathPart = `${album.type}/${album.language}/${album.genre}/${album.subgenre}/${album.artist}/${album.album}/`;
            if (normalizedPath.includes(expectedPathPart)) {
              return true;
            }

            // Strategy 1a: Without artist folder
            const escapedAlbum = album.album.replace(
              /[.*+?^${}()|[\]\\]/g,
              '\\$&'
            );
            const exactAlbumRegex1a = new RegExp(
              `(^|[/\\\\])${album.type}/${album.language}/${album.genre}/${album.subgenre}/${escapedAlbum}([/\\\\]|$)`
            );
            if (exactAlbumRegex1a.test(normalizedPath)) {
              return true;
            }

            // Strategy 1c: Without genre folder
            const exactAlbumRegex1c = new RegExp(
              `(^|[/\\\\])${album.type}/${album.language}/${album.subgenre}/${escapedAlbum}([/\\\\]|$)`
            );
            if (exactAlbumRegex1c.test(normalizedPath)) {
              return true;
            }

            // Strategy 1b: With base path
            if (basePath && normalizedPath.startsWith(basePath)) {
              const relativePath = normalizedPath.substring(basePath.length);
              const exactAlbumRegex = new RegExp(
                `(^|[/\\\\])${escapedAlbum}([/\\\\]|$)`
              );
              const exactAlbumMatch = exactAlbumRegex.test(relativePath);
              const basePathHasNoGenre =
                basePath.includes(
                  `${album.type}/${album.language}/${album.subgenre}/`
                ) && !basePath.includes(`/${album.genre}/`);
              const hasArtistMatch =
                isCompilation ||
                basePathHasNoGenre ||
                relativePath.includes(album.artist);

              if (hasArtistMatch && exactAlbumMatch) {
                return true;
              }
            }

            return false;
          }
        });

        if (albumFiles.length > 0 || albumImageFiles.length > 0) {
          const allFiles = [...albumFiles, ...albumImageFiles];
          filesByAlbum[album.id] = { album, files: allFiles, path: albumPath };
          console.log(
            `   ✅ Found ${albumFiles.length} audio files and ${albumImageFiles.length} image files for this album`
          );
        } else {
          console.log(`   ❌ No files matched for this album`);
          // Show sample file paths to help debug
          if (files.length > 0) {
            console.log(`   Sample file paths:`);
            files.slice(0, 3).forEach((f) => {
              console.log(
                `     - ${(f.webkitRelativePath || f.name).replace(/\\/g, '/')}`
              );
            });
          }
        }
      }

      console.log(`\n📊 Upload Summary:`);
      console.log(
        `   Total albums with files: ${Object.keys(filesByAlbum).length}`
      );
      Object.entries(filesByAlbum).forEach(([albumId, { album, files }]) => {
        console.log(
          `   - ${album.artist} - ${album.album}: ${files.length} files`
        );
      });

      if (Object.keys(filesByAlbum).length === 0) {
        console.error('❌ No files matched to any albums!');
        setUploadStatus(
          '❌ No files matched to selected albums. Check console for details.'
        );
        setUploading(false);
        return;
      }

      setUploadStatus(
        `Found files for ${
          Object.keys(filesByAlbum).length
        } albums. Uploading...`
      );

      // Upload files for each album
      for (const [albumId, { album, files, path }] of Object.entries(
        filesByAlbum
      )) {
        console.log(
          `\n📤 Starting upload for: ${album.artist} - ${album.album}`
        );
        console.log(`   Files to upload: ${files.length}`);
        console.log(`   Upload path: ${path}`);

        setUploadStatus(
          `Uploading: ${album.artist} - ${album.album} (${files.length} files)`
        );

        let albumSuccess = true;

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const fileName = file.name;
          const storagePath = `${path}${fileName}`;

          try {
            const storageRef = ref(storage, storagePath);
            const uploadTask = uploadBytesResumable(storageRef, file);

            await new Promise((resolve, reject) => {
              uploadTask.on(
                'state_changed',
                (snapshot) => {
                  const progress =
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                  setUploadProgress((prev) => ({
                    ...prev,
                    [albumId]: {
                      current: i + 1,
                      total: files.length,
                      fileName: fileName,
                      progress: progress,
                    },
                  }));
                },
                (error) => {
                  console.error('Upload error:', error);
                  albumSuccess = false;
                  reject(error);
                },
                () => resolve()
              );
            });
          } catch (error) {
            console.error(`Error uploading ${fileName}:`, error);
            albumSuccess = false;
            errorCount++;
          }
        }

        // Update Firestore if all files uploaded successfully
        if (albumSuccess) {
          try {
            // Find cover image file if uploaded
            const coverImageFile = files.find((f) => {
              const fileName = f.name.toLowerCase();
              return (
                fileName.endsWith('.jpg') ||
                fileName.endsWith('.jpeg') ||
                fileName.endsWith('.png') ||
                fileName.endsWith('.webp')
              );
            });

            const updateData = {
              storageStatus: 'available',
              uploadedAt: new Date().toISOString(),
            };

            // If cover image was uploaded, update coverUrl
            if (coverImageFile) {
              const coverFileName = coverImageFile.name;
              // Construct coverUrl path (without the type prefix, starting with /)
              if (album.type === 'Singles') {
                updateData.coverUrl = `/${album.language}/Various/Variety/Mix/${album.album}/${coverFileName}`;
              } else {
                updateData.coverUrl = `/${album.language}/${album.genre}/${album.subgenre}/${album.artist}/${album.album}/${coverFileName}`;
              }
              console.log(`   📷 Cover image uploaded: ${coverFileName}`);
              console.log(`   📷 Cover URL set to: ${updateData.coverUrl}`);
            }

            await updateDoc(doc(db, 'albums-flat', albumId), updateData);
            uploadedCount++;

            // Remove from unavailable list
            setUnavailableAlbums((prev) =>
              prev.filter((a) => a.id !== albumId)
            );
            setSelectedAlbums((prev) => {
              const newSet = new Set(prev);
              newSet.delete(albumId);
              return newSet;
            });
          } catch (error) {
            console.error('Error updating Firestore:', error);
          }
        }
      }

      setUploadStatus(
        `✅ Upload complete! ${uploadedCount} albums uploaded successfully. ${
          errorCount > 0 ? `${errorCount} errors.` : ''
        }`
      );
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus(`❌ Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
      setTimeout(() => {
        setShowUploadModal(false);
        setUploadProgress({});
      }, 3000);
    }
  };

  // Sync storage status - check if files exist and update Firestore
  const syncStorageStatus = async () => {
    setSyncing(true);
    setSyncResults(null);
    syncCancelledRef.current = false;

    try {
      let checkedCount = 0;
      let foundCount = 0;
      let updatedToAvailable = 0;
      let updatedToUnavailable = 0;
      let updatedCount = 0;
      let errorCount = 0;
      const errors = [];

      // Use filtered albums (respects current filter selections) - only check what's in current view
      const filteredUnavailable = getFilteredAlbums();

      // If user has selected specific albums, only check those; otherwise check all filtered albums
      const albumsToCheck =
        selectedAlbums.size > 0
          ? filteredUnavailable.filter((a) => selectedAlbums.has(a.id))
          : filteredUnavailable;

      setSyncProgress({
        current: 0,
        total: albumsToCheck.length,
        currentAlbum: '',
      });
      console.log(`Checking ${albumsToCheck.length} albums in current view...`);

      for (const album of albumsToCheck) {
        // Check if sync was cancelled
        if (syncCancelledRef.current) {
          console.log('🛑 Sync cancelled by user');
          setSyncResults({
            checked: checkedCount,
            found: foundCount,
            updated: updatedCount,
            errors: errorCount,
            cancelled: true,
          });
          break;
        }

        checkedCount++;
        setSyncProgress({
          current: checkedCount,
          total: albumsToCheck.length,
          currentAlbum: `${album.artist} - ${album.album}`,
        });

        // Use coverUrl from Firestore document - it contains the actual storage path
        // DO NOT construct paths - always use what's in Firestore
        let coverPath = album.coverUrl;
        if (!coverPath) {
          // No coverUrl in Firestore - cannot verify, mark as unavailable
          console.log(
            `  ⚠️ No coverUrl in Firestore for: ${album.artist} - ${album.album}`
          );
          try {
            await updateDoc(doc(db, 'albums-flat', album.id), {
              storageStatus: 'unavailable',
              syncedAt: new Date().toISOString(),
            });
            updatedCount++;
            updatedToUnavailable++;
            console.log(
              `  ⚠️ Updated to unavailable (no coverUrl in Firestore)`
            );
          } catch (updateError) {
            errorCount++;
            const errorMsg =
              updateError.message || updateError.code || 'Unknown error';
            errors.push(`${album.artist} - ${album.album}: ${errorMsg}`);
            console.error(`  ❌ Error updating doc: ${errorMsg}`);
          }
          continue; // Skip to next album
        }

        // Process the coverUrl from Firestore
        if (coverPath.startsWith('/')) {
          coverPath = coverPath.substring(1);
        }
        const type = album.type || 'Albums';
        if (!coverPath.startsWith(type + '/')) {
          coverPath = `${type}/${coverPath}`;
        }

        console.log(`Checking: ${album.artist} - ${album.album}`);
        console.log(`  Cover path from Firestore: ${coverPath}`);

        try {
          const coverRef = ref(storage, coverPath);
          await getDownloadURL(coverRef);

          console.log(`  ✅ Cover image FOUND in storage!`);

          // Check if there are .mp3 files in the same directory
          const directoryPath = coverPath.substring(
            0,
            coverPath.lastIndexOf('/') + 1
          );
          let hasMp3Files = false;

          try {
            const directoryRef = ref(storage, directoryPath);
            const filesList = await listAll(directoryRef);

            // Check if any file has .mp3 extension (case insensitive)
            hasMp3Files = filesList.items.some((item) => {
              const fileName = item.name.toLowerCase();
              return fileName.endsWith('.mp3');
            });

            console.log(
              `  ${hasMp3Files ? '✅' : '❌'} MP3 files: ${
                hasMp3Files ? 'Found' : 'Not found'
              }`
            );
          } catch (listError) {
            console.log(
              `  ⚠️ Could not list files in directory ${directoryPath}: ${listError.message}`
            );
            hasMp3Files = false;
          }

          // Only mark as available if both cover image AND .mp3 files exist
          if (hasMp3Files) {
            // Both cover image and MP3 files exist! Update Firestore to available
            try {
              await updateDoc(doc(db, 'albums-flat', album.id), {
                storageStatus: 'available',
                syncedAt: new Date().toISOString(),
              });
              updatedCount++;
              updatedToAvailable++;
              foundCount++;
              console.log(`  ✅ Updated to available (has cover + MP3 files)`);
            } catch (updateError) {
              // Handle Firestore update errors separately
              const updateErrorCode = updateError.code || updateError.message;
              if (updateErrorCode.includes('permission-denied')) {
                console.log(
                  `  ⚠️ Found in storage but cannot update Firestore (permission denied). File exists but status not updated.`
                );
                errorCount++;
                errors.push(
                  `${album.artist} - ${album.album}: Permission denied when updating Firestore`
                );
              } else {
                console.error(
                  `  ❌ Error updating Firestore: ${updateErrorCode}`
                );
                errorCount++;
                errors.push(
                  `${album.artist} - ${album.album}: ${updateErrorCode}`
                );
              }
            }
          } else {
            // Cover image exists but no MP3 files - mark as unavailable
            try {
              await updateDoc(doc(db, 'albums-flat', album.id), {
                storageStatus: 'unavailable',
                syncedAt: new Date().toISOString(),
              });
              updatedCount++;
              updatedToUnavailable++;
              console.log(`  ⚠️ Updated to unavailable (no MP3 files found)`);
            } catch (updateError) {
              errorCount++;
              const errorMsg =
                updateError.message || updateError.code || 'Unknown error';
              errors.push(`${album.artist} - ${album.album}: ${errorMsg}`);
              console.error(`  ❌ Error updating doc: ${errorMsg}`);
            }
          }
        } catch (error) {
          // File doesn't exist in storage - use the path from Firestore document
          const errorCode = error.code || error.message;
          console.log(`  ❌ NOT FOUND in storage: ${errorCode}`);
          console.log(
            `  Using path from Firestore coverUrl: ${album.coverUrl || 'N/A'}`
          );

          // Only mark as unavailable if it's a not-found error
          if (
            errorCode.includes('not-found') ||
            errorCode.includes('object-not-found')
          ) {
            // File doesn't exist at the path specified in Firestore - mark as unavailable
            try {
              await updateDoc(doc(db, 'albums-flat', album.id), {
                storageStatus: 'unavailable',
                syncedAt: new Date().toISOString(),
              });
              updatedCount++;
              updatedToUnavailable++;
              console.log(
                `  ⚠️ Updated to unavailable (file not found at path from Firestore)`
              );
            } catch (updateError) {
              errorCount++;
              const errorMsg =
                updateError.message || updateError.code || 'Unknown error';
              errors.push(`${album.artist} - ${album.album}: ${errorMsg}`);
              console.error(`  ❌ Error updating doc: ${errorMsg}`);
            }
          } else {
            // Unexpected error type
            errorCount++;
            errors.push(`${album.artist} - ${album.album}: ${errorCode}`);
          }
        }
        // Note: If coverUrl doesn't exist, we already handled it above and continued to next album

        // Update progress
        if (checkedCount % 10 === 0) {
          console.log(`Checked ${checkedCount}/${albumsToCheck.length}...`);
        }
      }

      setSyncResults({
        checked: checkedCount,
        found: foundCount,
        updated: updatedCount,
        updatedToAvailable: updatedToAvailable,
        updatedToUnavailable: updatedToUnavailable,
        errors: errorCount,
      });

      if (errors.length > 0 && errors.length <= 5) {
        console.log('\n⚠️ Errors encountered:');
        errors.forEach((err) => console.log(`  - ${err}`));
      } else if (errors.length > 5) {
        console.log(`\n⚠️ ${errors.length} errors encountered. First 5:`);
        errors.slice(0, 5).forEach((err) => console.log(`  - ${err}`));
      }

      console.log(
        `✅ Sync complete: ${foundCount}/${checkedCount} albums found in storage (with cover + MP3 files), ${updatedToAvailable} marked as available, ${updatedToUnavailable} marked as unavailable, ${updatedCount} total updated`
      );

      // Reload unavailable albums from Firestore to reflect changes
      if (updatedCount > 0) {
        console.log('Reloading unavailable albums...');
        const q = query(
          collection(db, 'albums-flat'),
          where('storageStatus', '==', 'unavailable')
        );
        const querySnapshot = await getDocs(q);

        const albums = [];
        querySnapshot.forEach((doc) => {
          albums.push({ id: doc.id, ...doc.data() });
        });

        setUnavailableAlbums(albums);
        setSelectedAlbums(new Set()); // Clear selections
        console.log(`Reloaded: ${albums.length} unavailable albums remaining`);
      }
    } catch (error) {
      console.error('Sync error:', error);
      alert(`Error syncing: ${error.message}`);
    } finally {
      setSyncing(false);
      setSyncProgress({ current: 0, total: 0, currentAlbum: '' });
      syncCancelledRef.current = false;
    }
  };

  // Reverse sync - verify albums marked as "available" actually exist in Storage
  const reverseSyncStorageStatus = async () => {
    setSyncing(true);
    setSyncResults(null);
    syncCancelledRef.current = false;

    try {
      let checkedCount = 0;
      let notFoundCount = 0;
      let updatedCount = 0;
      let errorCount = 0;
      const errors = [];

      // Get all albums marked as "available" in Firestore
      const q = query(
        collection(db, 'albums-flat'),
        where('storageStatus', '==', 'available')
      );

      const querySnapshot = await getDocs(q);
      const availableAlbums = [];
      querySnapshot.forEach((doc) => {
        availableAlbums.push({ id: doc.id, ...doc.data() });
      });

      setSyncProgress({
        current: 0,
        total: availableAlbums.length,
        currentAlbum: '',
      });
      console.log(
        `🔄 Reverse Sync: Checking ${availableAlbums.length} albums marked as "available"...`
      );

      for (const album of availableAlbums) {
        // Check if sync was cancelled
        if (syncCancelledRef.current) {
          console.log('🛑 Reverse sync cancelled by user');
          setSyncResults({
            checked: checkedCount,
            notFound: notFoundCount,
            updated: updatedCount,
            errors: errorCount,
            cancelled: true,
          });
          break;
        }

        checkedCount++;
        setSyncProgress({
          current: checkedCount,
          total: availableAlbums.length,
          currentAlbum: `${album.artist} - ${album.album}`,
        });

        // Use coverUrl from Firestore document - it should contain the actual storage path
        let coverPath = album.coverUrl;
        if (!coverPath) {
          // No coverUrl in Firestore - mark as unavailable since we can't verify
          console.log(
            `  ⚠️ No coverUrl in Firestore for: ${album.artist} - ${album.album}`
          );
          try {
            await updateDoc(doc(db, 'albums-flat', album.id), {
              storageStatus: 'unavailable',
              syncedAt: new Date().toISOString(),
            });
            updatedCount++;
            console.log(
              `  ⚠️ Updated to unavailable (no coverUrl in Firestore)`
            );
          } catch (updateError) {
            errorCount++;
            const errorMsg =
              updateError.message || updateError.code || 'Unknown error';
            errors.push(`${album.artist} - ${album.album}: ${errorMsg}`);
            console.error(`  ❌ Error updating doc: ${errorMsg}`);
          }
          continue; // Skip to next album
        }

        // Process the coverUrl from Firestore
        if (coverPath.startsWith('/')) {
          coverPath = coverPath.substring(1);
        }
        const type = album.type || 'Albums';
        if (!coverPath.startsWith(type + '/')) {
          coverPath = `${type}/${coverPath}`;
        }

        console.log(`Checking: ${album.artist} - ${album.album}`);
        console.log(`  Cover path: ${coverPath}`);

        try {
          const coverRef = ref(storage, coverPath);
          await getDownloadURL(coverRef);

          console.log(`  ✅ Cover EXISTS in storage`);

          // Check if there are .mp3 files in the same directory
          const directoryPath = coverPath.substring(
            0,
            coverPath.lastIndexOf('/') + 1
          );
          let hasMp3Files = false;

          try {
            const directoryRef = ref(storage, directoryPath);
            const filesList = await listAll(directoryRef);

            // Check if any file has .mp3 extension (case insensitive)
            hasMp3Files = filesList.items.some((item) => {
              const fileName = item.name.toLowerCase();
              return fileName.endsWith('.mp3');
            });

            console.log(
              `  ${hasMp3Files ? '✅' : '❌'} MP3 files: ${
                hasMp3Files ? 'Found' : 'Not found'
              }`
            );
          } catch (listError) {
            console.log(
              `  ⚠️ Could not list files in directory ${directoryPath}: ${listError.message}`
            );
            hasMp3Files = false;
          }

          // If cover exists but no MP3 files, mark as unavailable
          if (!hasMp3Files) {
            try {
              await updateDoc(doc(db, 'albums-flat', album.id), {
                storageStatus: 'unavailable',
                syncedAt: new Date().toISOString(),
                reverseSyncedAt: new Date().toISOString(),
              });
              updatedCount++;
              notFoundCount++;
              console.log(
                `  ⚠️ Marked as unavailable (cover exists but no MP3 files found)`
              );
            } catch (updateError) {
              const updateErrorCode = updateError.code || updateError.message;
              console.error(
                `  ❌ Error updating Firestore: ${updateErrorCode}`
              );
              errorCount++;
              errors.push(
                `${album.artist} - ${album.album}: ${updateErrorCode}`
              );
            }
          } else {
            console.log(`  ✅ Keeping as available (has cover + MP3 files)`);
          }
        } catch (error) {
          // File doesn't exist in storage - use the path from Firestore document
          const errorCode = error.code || error.message;
          console.log(`  ❌ NOT FOUND in storage: ${errorCode}`);
          console.log(
            `  Using path from Firestore coverUrl: ${album.coverUrl || 'N/A'}`
          );

          // Only mark as unavailable if it's a not-found error
          if (
            errorCode.includes('not-found') ||
            errorCode.includes('object-not-found')
          ) {
            // File doesn't exist at the path specified in Firestore - mark as unavailable
            try {
              await updateDoc(doc(db, 'albums-flat', album.id), {
                storageStatus: 'unavailable',
                syncedAt: new Date().toISOString(),
                reverseSyncedAt: new Date().toISOString(),
              });
              updatedCount++;
              notFoundCount++;
              console.log(
                `  ⚠️ Marked as unavailable (file not found at path from Firestore)`
              );
            } catch (updateError) {
              const updateErrorCode = updateError.code || updateError.message;
              console.error(
                `  ❌ Error updating Firestore: ${updateErrorCode}`
              );
              errorCount++;
              errors.push(
                `${album.artist} - ${album.album}: ${updateErrorCode}`
              );
            }
          } else {
            // Unexpected error (not a simple "not found")
            errorCount++;
            errors.push(`${album.artist} - ${album.album}: ${errorCode}`);
          }
        }

        // Update progress
        if (checkedCount % 10 === 0) {
          console.log(`Checked ${checkedCount}/${availableAlbums.length}...`);
        }
      }

      setSyncResults({
        checked: checkedCount,
        notFound: notFoundCount,
        updated: updatedCount,
        errors: errorCount,
      });

      if (errors.length > 0 && errors.length <= 5) {
        console.log('\n⚠️ Errors encountered:');
        errors.forEach((err) => console.log(`  - ${err}`));
      } else if (errors.length > 5) {
        console.log(`\n⚠️ ${errors.length} errors encountered. First 5:`);
        errors.slice(0, 5).forEach((err) => console.log(`  - ${err}`));
      }

      console.log(
        `✅ Reverse sync complete: ${notFoundCount}/${checkedCount} albums not found in Storage, ${updatedCount} marked as unavailable`
      );

      // Reload unavailable albums from Firestore to reflect changes
      if (updatedCount > 0) {
        console.log('Reloading unavailable albums...');
        const unavailableQuery = query(
          collection(db, 'albums-flat'),
          where('storageStatus', '==', 'unavailable')
        );
        const unavailableSnapshot = await getDocs(unavailableQuery);

        const albums = [];
        unavailableSnapshot.forEach((doc) => {
          albums.push({ id: doc.id, ...doc.data() });
        });

        setUnavailableAlbums(albums);
        setSelectedAlbums(new Set()); // Clear selections
        console.log(`Reloaded: ${albums.length} unavailable albums`);
      }
    } catch (error) {
      console.error('Reverse sync error:', error);
      alert(`Error reverse syncing: ${error.message}`);
    } finally {
      setSyncing(false);
      setSyncProgress({ current: 0, total: 0, currentAlbum: '' });
      syncCancelledRef.current = false;
    }
  };

  // Sync TO Docs: Update Firestore docs based on what exists in Storage
  const syncToDocs = async () => {
    setSyncing(true);
    setSyncResults(null);
    syncCancelledRef.current = false;

    try {
      let checkedCount = 0;
      let foundCount = 0;
      let updatedToAvailable = 0;
      let updatedToUnavailable = 0;
      let updatedCount = 0;
      let errorCount = 0;
      const errors = [];

      // Use filtered albums (respects current filter selections)
      const filteredAlbums = getFilteredAlbums();

      // If user has selected specific albums, only check those; otherwise check all filtered albums
      const albumsToCheck =
        selectedAlbums.size > 0
          ? filteredAlbums.filter((a) => selectedAlbums.has(a.id))
          : filteredAlbums;

      setSyncProgress({
        current: 0,
        total: albumsToCheck.length,
        currentAlbum: '',
      });
      console.log(
        `🔄 Sync to Docs: Checking ${albumsToCheck.length} albums in current view...`
      );

      for (const album of albumsToCheck) {
        // Check if sync was cancelled
        if (syncCancelledRef.current) {
          console.log('🛑 Sync to Docs cancelled by user');
          setSyncResults({
            checked: checkedCount,
            found: foundCount,
            updated: updatedCount,
            errors: errorCount,
            cancelled: true,
          });
          break;
        }

        checkedCount++;
        setSyncProgress({
          current: checkedCount,
          total: albumsToCheck.length,
          currentAlbum: `${album.artist} - ${album.album}`,
        });

        // Use coverUrl from Firestore document - it contains the actual storage path
        // DO NOT construct paths - always use what's in Firestore
        let coverPath = album.coverUrl;
        if (!coverPath) {
          // No coverUrl in Firestore - cannot verify, mark as unavailable
          console.log(
            `  ⚠️ No coverUrl in Firestore for: ${album.artist} - ${album.album}`
          );
          try {
            await updateDoc(doc(db, 'albums-flat', album.id), {
              storageStatus: 'unavailable',
              syncedAt: new Date().toISOString(),
            });
            updatedCount++;
            updatedToUnavailable++;
            console.log(
              `  ⚠️ Updated to unavailable (no coverUrl in Firestore)`
            );
          } catch (updateError) {
            errorCount++;
            const errorMsg =
              updateError.message || updateError.code || 'Unknown error';
            errors.push(`${album.artist} - ${album.album}: ${errorMsg}`);
            console.error(`  ❌ Error updating doc: ${errorMsg}`);
          }
          continue; // Skip to next album
        }

        // Process the coverUrl from Firestore
        // Remove leading slash if present and ensure type prefix
        if (coverPath.startsWith('/')) {
          coverPath = coverPath.substring(1);
        }
        const type = album.type || 'Albums';
        if (!coverPath.startsWith(type + '/')) {
          coverPath = `${type}/${coverPath}`;
        }

        // Check if cover image exists at the exact path from Firestore
        let foundInStorage = false;
        try {
          const coverRef = ref(storage, coverPath);
          await getDownloadURL(coverRef);
          foundInStorage = true;
        } catch (error) {
          // Cover image doesn't exist at the path from Firestore
          foundInStorage = false;
        }

        if (foundInStorage) {
          // Check if there are .mp3 files in the same directory
          // Get the directory path (remove filename from coverPath)
          const directoryPath = coverPath.substring(
            0,
            coverPath.lastIndexOf('/') + 1
          );
          let hasMp3Files = false;

          try {
            const directoryRef = ref(storage, directoryPath);
            const filesList = await listAll(directoryRef);

            // Check if any file has .mp3 extension (case insensitive)
            hasMp3Files = filesList.items.some((item) => {
              const fileName = item.name.toLowerCase();
              return fileName.endsWith('.mp3');
            });
          } catch (listError) {
            console.log(
              `  ⚠️ Could not list files in directory ${directoryPath}: ${listError.message}`
            );
            hasMp3Files = false;
          }

          // Only mark as available if both album.jpg AND .mp3 files exist
          if (hasMp3Files) {
            // Both cover image and MP3 files exist - update Firestore doc
            try {
              const updateData = {
                storageStatus: 'available',
                syncedAt: new Date().toISOString(),
              };

              // coverUrl is already correct (from Firestore), no need to update it

              await updateDoc(doc(db, 'albums-flat', album.id), updateData);
              updatedCount++;
              updatedToAvailable++;
              foundCount++;
              console.log(
                `  ✅ Updated doc: ${album.artist} - ${album.album} → available (has cover + MP3 files)`
              );
            } catch (updateError) {
              errorCount++;
              const errorMsg =
                updateError.message || updateError.code || 'Unknown error';
              errors.push(`${album.artist} - ${album.album}: ${errorMsg}`);
              console.error(`  ❌ Error updating doc: ${errorMsg}`);
            }
          } else {
            // Cover image exists but no MP3 files - mark as unavailable
            try {
              await updateDoc(doc(db, 'albums-flat', album.id), {
                storageStatus: 'unavailable',
                syncedAt: new Date().toISOString(),
              });
              updatedCount++;
              updatedToUnavailable++;
              console.log(
                `  ⚠️ Updated doc: ${album.artist} - ${album.album} → unavailable (no MP3 files found)`
              );
            } catch (updateError) {
              errorCount++;
              const errorMsg =
                updateError.message || updateError.code || 'Unknown error';
              errors.push(`${album.artist} - ${album.album}: ${errorMsg}`);
              console.error(`  ❌ Error updating doc: ${errorMsg}`);
            }
          }
        } else {
          // File doesn't exist in Storage - mark as unavailable
          try {
            await updateDoc(doc(db, 'albums-flat', album.id), {
              storageStatus: 'unavailable',
              syncedAt: new Date().toISOString(),
            });
            updatedCount++;
            updatedToUnavailable++;
            console.log(
              `  ⚠️ Updated doc: ${album.artist} - ${album.album} → unavailable`
            );
          } catch (updateError) {
            errorCount++;
            const errorMsg =
              updateError.message || updateError.code || 'Unknown error';
            errors.push(`${album.artist} - ${album.album}: ${errorMsg}`);
            console.error(`  ❌ Error updating doc: ${errorMsg}`);
          }
        }

        // Update progress
        if (checkedCount % 10 === 0) {
          console.log(`Checked ${checkedCount}/${albumsToCheck.length}...`);
        }
      }

      setSyncResults({
        checked: checkedCount,
        found: foundCount,
        updated: updatedCount,
        updatedToAvailable: updatedToAvailable,
        updatedToUnavailable: updatedToUnavailable,
        errors: errorCount,
      });

      if (errors.length > 0 && errors.length <= 5) {
        console.log('\n⚠️ Errors encountered:');
        errors.forEach((err) => console.log(`  - ${err}`));
      } else if (errors.length > 5) {
        console.log(`\n⚠️ ${errors.length} errors encountered. First 5:`);
        errors.slice(0, 5).forEach((err) => console.log(`  - ${err}`));
      }

      console.log(
        `✅ Sync to Docs complete: ${foundCount}/${checkedCount} albums found in Storage, ${updatedCount} docs updated`
      );

      // Reload albums from Firestore to reflect changes
      if (updatedCount > 0) {
        console.log('Reloading albums...');
        const unavailableQuery = query(
          collection(db, 'albums-flat'),
          where('storageStatus', '==', 'unavailable')
        );
        const unavailableSnapshot = await getDocs(unavailableQuery);

        const albums = [];
        unavailableSnapshot.forEach((doc) => {
          albums.push({ id: doc.id, ...doc.data() });
        });

        setUnavailableAlbums(albums);
        setSelectedAlbums(new Set()); // Clear selections
        console.log(`Reloaded: ${albums.length} unavailable albums`);
      }
    } catch (error) {
      console.error('Sync to Docs error:', error);
      alert(`Error syncing to docs: ${error.message}`);
    } finally {
      setSyncing(false);
      setSyncProgress({ current: 0, total: 0, currentAlbum: '' });
      syncCancelledRef.current = false;
    }
  };

  // Cancel sync
  const cancelSync = () => {
    syncCancelledRef.current = true;
    setSyncing(false);
  };

  // Debug logging (must be before early returns)
  useEffect(() => {
    if (!loading && allAlbums.length > 0) {
      const languages = getUniqueValues('language');
      const genres = getUniqueValues('genre');
      const subgenres = getUniqueValues('subgenre');
      const artists = getUniqueValues('artist');
      const albums = getUniqueValues('album');

      // Debug: Always check Albums/English structure (regardless of current filters)
      const englishAlbums = allAlbums.filter((a) => {
        const albumType = (a.type || a.Type || '')
          .toString()
          .trim()
          .toLowerCase();
        const albumLang = (a.language || a.Language || '')
          .toString()
          .trim()
          .toLowerCase();
        return albumType === 'albums' && albumLang === 'english';
      });

      if (englishAlbums.length > 0) {
        const genresInEnglish = new Set();
        const subgenresInEnglish = new Set();
        const artistsInEnglish = new Set();
        englishAlbums.forEach((a) => {
          const genre = (a.genre || a.Genre || '').toString().trim();
          const subgenre = (a.subgenre || a.Subgenre || '').toString().trim();
          const artist = (a.artist || a.artistName || a.Artist || '')
            .toString()
            .trim();
          if (genre) genresInEnglish.add(genre);
          if (subgenre) subgenresInEnglish.add(subgenre);
          if (artist) artistsInEnglish.add(artist);
        });

        // Check specifically for "Decades" genre and "Various Artists"
        const decadesAlbums = englishAlbums.filter((a) => {
          const genre = (a.genre || a.Genre || '')
            .toString()
            .trim()
            .toLowerCase();
          return genre === 'decades';
        });

        const variousArtistsAlbums = englishAlbums.filter((a) => {
          const artist = (a.artist || a.artistName || a.Artist || '')
            .toString()
            .trim();
          return artist.toLowerCase().includes('various');
        });

        // Check what happens when filtering by Decades
        const decadesFiltered = englishAlbums.filter((a) => {
          const genre = (a.genre || a.Genre || '')
            .toString()
            .trim()
            .toLowerCase();
          return genre === 'decades';
        });

        const decadesArtists = new Set();
        decadesFiltered.forEach((a) => {
          const artist = (a.artist || a.artistName || a.Artist || '')
            .toString()
            .trim();
          if (artist) decadesArtists.add(artist);
        });

        console.log('🔍 Albums/English Debug (Always):', {
          totalEnglishAlbums: englishAlbums.length,
          genres: Array.from(genresInEnglish).sort(),
          subgenres: Array.from(subgenresInEnglish).sort(),
          artists: Array.from(artistsInEnglish).sort().slice(0, 30), // First 30 artists
          decadesAlbumsCount: decadesAlbums.length,
          decadesArtists: Array.from(decadesArtists).sort(),
          variousArtistsAlbumsCount: variousArtistsAlbums.length,
          currentFilters: filters,
          sampleDecadesAlbums: decadesAlbums.slice(0, 5).map((a) => ({
            genre: a.genre || a.Genre,
            subgenre: a.subgenre || a.Subgenre,
            artist: a.artist || a.artistName || a.Artist,
            album: a.album || a.albumName,
            storageStatus: a.storageStatus,
          })),
          sampleVariousArtistsAlbums: variousArtistsAlbums
            .slice(0, 5)
            .map((a) => ({
              genre: a.genre || a.Genre,
              subgenre: a.subgenre || a.Subgenre,
              artist: a.artist || a.artistName || a.Artist,
              album: a.album || a.albumName,
              storageStatus: a.storageStatus,
            })),
        });
      }

      // Check what types exist
      const typeCounts = {};
      const languageCounts = {};
      unavailableAlbums.forEach((album) => {
        const type = album.type || album.Type || 'undefined';
        typeCounts[type] = (typeCounts[type] || 0) + 1;

        const lang = album.language || album.Language;
        if (lang) {
          languageCounts[lang] = (languageCounts[lang] || 0) + 1;
        }
      });

      // Check what languages exist when type filter is applied
      let filteredByType = unavailableAlbums;
      if (filters.type !== 'all') {
        filteredByType = unavailableAlbums.filter((a) => {
          const albumType = a.type || a.Type;
          return albumType === filters.type;
        });
      }

      const languageCountsFiltered = {};
      filteredByType.forEach((album) => {
        const lang = album.language || album.Language;
        if (lang) {
          languageCountsFiltered[lang] =
            (languageCountsFiltered[lang] || 0) + 1;
        }
      });

      console.log('📊 Upload Manager Debug:', {
        totalAlbums: unavailableAlbums.length,
        filters,
        typeCounts,
        languageCounts,
        languageCountsFiltered,
        languages: languages.length,
        languagesList: languages,
        genres: genres.length,
        subgenres: subgenres.length,
        artists: artists.length,
        albums: albums.length,
        sampleAlbum: unavailableAlbums[0],
        sampleAlbumKeys: Object.keys(unavailableAlbums[0] || {}),
        filteredByTypeCount: filteredByType.length,
      });

      // Log first few albums when type filter is active
      if (filters.type !== 'all') {
        console.log(
          `🔍 Albums with type="${filters.type}":`,
          filteredByType.length
        );
        if (filteredByType.length > 0) {
          console.log('Sample filtered album:', filteredByType[0]);
          console.log(
            'Sample filtered album language:',
            filteredByType[0].language || filteredByType[0].Language
          );
        } else {
          console.warn(`⚠️ No albums found with type="${filters.type}"`);
        }
      }
    }
  }, [loading, unavailableAlbums, filters]);

  // Use useMemo to ensure these are recalculated when albums or filters change
  // Must be before early returns to satisfy React Hooks rules
  // Note: We use allAlbums in getUniqueValues, so we should check allAlbums.length, not unavailableAlbums.length
  const languages = useMemo(() => {
    if (loading || allAlbums.length === 0) return [];
    const result = getUniqueValues('language');
    console.log('🔍 Computing languages:', result);
    return result;
  }, [allAlbums, filters, loading]);

  const genres = useMemo(() => {
    if (loading || allAlbums.length === 0) return [];
    return getUniqueValues('genre');
  }, [allAlbums, filters, loading]);

  const subgenres = useMemo(() => {
    if (loading || allAlbums.length === 0) return [];
    return getUniqueValues('subgenre');
  }, [allAlbums, filters, loading]);

  const artists = useMemo(() => {
    if (loading || allAlbums.length === 0) {
      console.log(
        '⏸️ artists useMemo: returning empty (loading or no albums)',
        {
          loading,
          allAlbumsLength: allAlbums.length,
        }
      );
      return [];
    }
    const result = getUniqueValues('artist');
    console.log('✅ artists useMemo: calculated artists', {
      count: result.length,
      artists: result,
      artistsArray: [...result], // Explicit array copy
      firstArtist: result[0],
      filters: filters,
    });
    if (result.length > 0) {
      console.log('📋 Artists list:', result.join(', '));
    } else {
      console.warn(
        '⚠️ Artists array is EMPTY even though getUniqueValues was called!'
      );
    }
    return result;
  }, [allAlbums, filters, loading]);

  const albums = useMemo(() => {
    if (loading || allAlbums.length === 0) return [];
    return getUniqueValues('album');
  }, [allAlbums, filters, loading]);

  const filteredAlbums = useMemo(() => {
    if (loading || unavailableAlbums.length === 0) return [];
    return getFilteredAlbums();
  }, [unavailableAlbums, filters, loading]);

  const groupedAlbums = useMemo(() => {
    if (loading || unavailableAlbums.length === 0) return {};
    return getGroupedAlbums();
  }, [unavailableAlbums, filters, loading]);

  if (loading) {
    return (
      <div className="upload-manager">
        <div className="loading">Loading unavailable albums...</div>
      </div>
    );
  }

  // Debug: Log what we're about to render
  console.log('🎨 Upload Manager Render:', {
    unavailableAlbumsCount: unavailableAlbums.length,
    filters,
    languagesCount: languages.length,
    languages: languages,
    genresCount: genres.length,
    hasAlbums: unavailableAlbums.length > 0,
    firstAlbum: unavailableAlbums[0]
      ? {
          id: unavailableAlbums[0].id,
          type: unavailableAlbums[0].type,
          language: unavailableAlbums[0].language,
          keys: Object.keys(unavailableAlbums[0]),
        }
      : null,
  });

  return (
    <div className="upload-manager">
      <div className="upload-header">
        <h1>📤 Upload Manager</h1>
        <p>Select albums you want to upload to Firebase Storage</p>
      </div>

      {/* Filters */}
      <div className="upload-filters">
        <div className="filter-group">
          <label>Type:</label>
          <select
            value={filters.type}
            onChange={(e) =>
              setFilters({
                ...filters,
                type: e.target.value,
                language: 'all',
                genre: 'all',
                subgenre: 'all',
                artist: 'all',
                album: 'all',
              })
            }
          >
            <option value="all">All Types</option>
            <option value="Albums">Albums</option>
            <option value="Singles">Singles</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Language:</label>
          <select
            value={filters.language}
            onChange={(e) =>
              setFilters({
                ...filters,
                language: e.target.value,
                genre: 'all',
                subgenre: 'all',
                artist: 'all',
                album: 'all',
              })
            }
          >
            <option value="all">All Languages</option>
            {(() => {
              console.log('🌐 Rendering Language dropdown:', {
                languages,
                count: languages.length,
                type: typeof languages,
                isArray: Array.isArray(languages),
              });
              return languages.length > 0 ? (
                languages.map((lang) => {
                  console.log('  - Rendering language option:', lang);
                  return (
                    <option key={lang} value={lang}>
                      {lang}
                    </option>
                  );
                })
              ) : (
                <option disabled>No languages found</option>
              );
            })()}
          </select>
        </div>

        <div className="filter-group">
          <label>Genre:</label>
          <select
            value={filters.genre}
            onChange={(e) =>
              setFilters({
                ...filters,
                genre: e.target.value,
                subgenre: 'all',
                artist: 'all',
                album: 'all',
              })
            }
          >
            <option value="all">All Genres</option>
            {genres.length > 0 ? (
              genres.map((genre) => (
                <option key={genre} value={genre}>
                  {genre}
                </option>
              ))
            ) : (
              <option disabled>No genres found</option>
            )}
          </select>
        </div>

        <div className="filter-group">
          <label>Subgenre:</label>
          <select
            value={filters.subgenre}
            onChange={(e) =>
              setFilters({
                ...filters,
                subgenre: e.target.value,
                artist: 'all',
                album: 'all',
              })
            }
          >
            <option value="all">All Subgenres</option>
            {subgenres.length > 0 ? (
              subgenres.map((subgenre) => (
                <option key={subgenre} value={subgenre}>
                  {subgenre}
                </option>
              ))
            ) : (
              <option disabled>No subgenres found</option>
            )}
          </select>
        </div>

        <div className="filter-group">
          <label>Artist:</label>
          {console.log('🎭 RENDERING Artist Dropdown:', {
            artistsLength: artists.length,
            artists: artists,
            artistsIsArray: Array.isArray(artists),
            filters: filters,
            willRenderOptions: artists.length > 0,
          })}
          <select
            value={filters.artist}
            onChange={(e) =>
              setFilters({ ...filters, artist: e.target.value, album: 'all' })
            }
          >
            <option value="all">All Artists</option>
            {artists.length > 0 ? (
              artists.map((artist) => {
                console.log('🎨 Rendering artist option:', artist);
                return (
                  <option key={artist} value={artist}>
                    {artist}
                  </option>
                );
              })
            ) : (
              <>
                {console.log('⚠️ Artists dropdown is empty!', {
                  artistsLength: artists.length,
                  artists: artists,
                  filters: filters,
                })}
                <option disabled>No artists found</option>
              </>
            )}
          </select>
        </div>

        <div className="filter-group">
          <label>Album:</label>
          <select
            value={filters.album}
            onChange={(e) => setFilters({ ...filters, album: e.target.value })}
          >
            <option value="all">All Albums</option>
            {albums.length > 0 ? (
              albums.map((album) => (
                <option key={album} value={album}>
                  {album}
                </option>
              ))
            ) : (
              <option disabled>No albums found</option>
            )}
          </select>
        </div>

        <div className="filter-group">
          <label>Group By:</label>
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
            <option value="language">Language</option>
            <option value="genre">Genre</option>
            <option value="artist">Artist</option>
            <option value="type">Type</option>
          </select>
        </div>

        <div className="filter-group">
          <button
            onClick={() => {
              setFilters({
                type: 'all',
                language: 'all',
                genre: 'all',
                subgenre: 'all',
                artist: 'all',
                album: 'all',
              });
            }}
            className="btn btn-secondary"
            style={{ marginTop: '20px' }}
            title="Clear all filters to show all unavailable albums"
          >
            🗑️ Clear Filters
          </button>
        </div>
      </div>

      {/* Current Path Display */}
      <div className="current-path-display">
        <strong>Current Path:</strong>{' '}
        {(() => {
          const pathParts = [];
          if (filters.type && filters.type !== 'all') {
            pathParts.push(filters.type);
            if (filters.language && filters.language !== 'all') {
              pathParts.push(filters.language);
              if (filters.type === 'Singles') {
                pathParts.push('Various', 'Variety');
                if (filters.artist && filters.artist !== 'all') {
                  pathParts.push(filters.artist, 'Mix');
                }
              } else {
                if (filters.genre && filters.genre !== 'all') {
                  pathParts.push(filters.genre);
                  if (filters.subgenre && filters.subgenre !== 'all') {
                    pathParts.push(filters.subgenre);
                    if (filters.artist && filters.artist !== 'all') {
                      pathParts.push(filters.artist);
                      if (filters.album && filters.album !== 'all') {
                        pathParts.push(filters.album);
                      }
                    }
                  }
                }
              }
            }
          }
          return pathParts.length > 0
            ? pathParts.join('/') + '/'
            : 'All albums';
        })()}
      </div>

      {/* Workflow Tip */}
      {!syncing && unavailableAlbums.length > 0 && (
        <div
          className="workflow-tip"
          style={{
            padding: '10px',
            marginBottom: '15px',
            backgroundColor: '#e3f2fd',
            borderRadius: '4px',
            fontSize: '14px',
            color: '#000',
          }}
        >
          <strong>💡 Recommended Workflow:</strong> Run{' '}
          <strong>Sync Status</strong> first to check what's already uploaded,
          then <strong>Upload Selected</strong> only the albums that are still
          unavailable.
        </div>
      )}

      {/* Stats & Actions */}
      <div className="upload-stats">
        <div className="stats-info">
          <span className="stat">
            <strong>{filteredAlbums.length}</strong> albums found
          </span>
          <span className="stat">
            <strong>{selectedAlbums.size}</strong> selected
          </span>
        </div>

        <div className="stats-actions">
          <button onClick={selectAll} className="btn btn-secondary">
            Select All
          </button>
          <button onClick={deselectAll} className="btn btn-secondary">
            Deselect All
          </button>
          {syncing ? (
            <button
              onClick={() => {
                syncCancelledRef.current = true;
                setSyncing(false);
              }}
              className="btn btn-cancel"
              title="Cancel sync operation (not recommended - let sync complete to avoid uploading duplicates)"
            >
              🛑 Cancel Sync ({syncProgress.current}/{syncProgress.total})
            </button>
          ) : (
            <>
              <button
                onClick={syncStorageStatus}
                className="btn btn-sync"
                disabled={filteredAlbums.length === 0}
                title="Check albums in current view against Storage. Marks unavailable albums as available if found."
              >
                🔄 Sync Status ({filteredAlbums.length})
              </button>
              <button
                onClick={reverseSyncStorageStatus}
                className="btn btn-sync"
                title="Verify albums marked as 'available' actually exist in Storage. Marks missing albums as 'unavailable'."
              >
                🔍 Reverse Sync (Verify Available)
              </button>
              <button
                onClick={checkDecadesAlbums}
                className="btn btn-secondary"
                disabled={syncing || loading}
                title="Check albums in Albums/English/Decades/60s/Various Artists/ path"
              >
                🔍 Check 60s Albums
              </button>
              <button
                onClick={markAll60sAlbumsUnavailable}
                className="btn btn-secondary"
                disabled={syncing || loading}
                title="Mark all 60s Decades albums as unavailable"
              >
                🔧 Mark All 60s as Unavailable
              </button>
              <button
                onClick={syncToDocs}
                className="btn btn-sync"
                disabled={filteredAlbums.length === 0}
                title="Sync TO Docs: Check all albums in current view against Storage and update Firestore docs (available/unavailable status)."
              >
                📝 Sync to Docs ({filteredAlbums.length})
              </button>
            </>
          )}
          <button
            onClick={copyPaths}
            className="btn btn-primary"
            disabled={selectedAlbums.size === 0}
          >
            📋 Copy Paths ({selectedAlbums.size})
          </button>
          <button
            onClick={exportSelected}
            className="btn btn-secondary"
            disabled={selectedAlbums.size === 0}
          >
            💾 Export JSON ({selectedAlbums.size})
          </button>
          <button
            onClick={startUpload}
            className="btn btn-upload"
            disabled={selectedAlbums.size === 0}
          >
            🚀 Upload Selected ({selectedAlbums.size})
          </button>
          <button
            onClick={() => {
              document.getElementById('cover-image-upload').click();
            }}
            className="btn btn-secondary"
            title="Upload cover images at all levels (subgenre, artist, album). If albums are selected, matches to those albums. Otherwise, auto-detects all album.jpg files in selected folder."
          >
            📷 Upload Cover Images Only
            {selectedAlbums.size > 0
              ? ` (${selectedAlbums.size})`
              : ' (Auto-detect)'}
          </button>
          <button
            onClick={() => {
              document.getElementById('full-library-upload').click();
            }}
            className="btn btn-secondary"
            title="Upload ALL album.jpg files from Albums/English at all levels (language, genre, subgenre, artist, album). Overwrites existing files."
          >
            📷 Upload All Images (Albums/English)
          </button>
          <input
            id="cover-image-upload"
            type="file"
            webkitdirectory=""
            directory=""
            multiple
            style={{ display: 'none' }}
            onChange={handleCoverImageUpload}
          />
          <input
            id="full-library-upload"
            type="file"
            webkitdirectory=""
            directory=""
            multiple
            style={{ display: 'none' }}
            onChange={handleFullLibraryImageUpload}
          />
        </div>
      </div>

      {/* Sync Results */}
      {syncing && syncProgress.total > 0 && (
        <div className="sync-progress">
          <div className="sync-progress-bar">
            <div
              className="sync-progress-fill"
              style={{
                width: `${(syncProgress.current / syncProgress.total) * 100}%`,
              }}
            ></div>
          </div>
          <div className="sync-progress-text">
            Checking {syncProgress.current} of {syncProgress.total} albums...
            {syncProgress.currentAlbum && (
              <span className="current-album">
                {' '}
                ({syncProgress.currentAlbum})
              </span>
            )}
          </div>
        </div>
      )}

      {syncResults && (
        <div className="sync-results">
          <div className="sync-results-content">
            {syncResults.cancelled ? (
              <>
                🛑 Sync Cancelled: Checked{' '}
                <strong>{syncResults.checked}</strong> albums before
                cancellation.
              </>
            ) : syncResults.notFound !== undefined ? (
              <>
                ✅ Reverse Sync Complete: Checked{' '}
                <strong>{syncResults.checked}</strong> albums marked as
                "available". Found <strong>{syncResults.notFound}</strong> that
                don't exist in Storage. Updated{' '}
                <strong>{syncResults.updated}</strong> album(s) to unavailable.
                {syncResults.errors > 0 &&
                  ` (${syncResults.errors} errors - check console)`}
              </>
            ) : (
              <>
                ✅ Sync Complete: Found <strong>{syncResults.found}</strong> of{' '}
                <strong>{syncResults.checked}</strong> albums in Storage (with
                cover + MP3 files).
                {syncResults.updatedToAvailable > 0 && (
                  <>
                    {' '}
                    Updated <strong>
                      {syncResults.updatedToAvailable}
                    </strong>{' '}
                    album(s) to available.
                  </>
                )}
                {syncResults.updatedToUnavailable > 0 && (
                  <>
                    {' '}
                    Updated <strong>
                      {syncResults.updatedToUnavailable}
                    </strong>{' '}
                    album(s) to unavailable.
                  </>
                )}
                {syncResults.updated === 0 && (
                  <> No albums needed status updates.</>
                )}
                {syncResults.errors > 0 &&
                  ` (${syncResults.errors} errors - check console)`}
              </>
            )}
            {syncResults.updated > 0 && (
              <div
                style={{
                  marginTop: '0.5rem',
                  fontSize: '0.9rem',
                  opacity: 0.9,
                }}
              >
                These albums are now available in your Library! Refresh your
                Library page to see them. 🎉
              </div>
            )}
          </div>
          <button
            onClick={() => setSyncResults(null)}
            className="sync-results-close"
          >
            ×
          </button>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div
          className="upload-modal-overlay"
          onClick={() => !uploading && setShowUploadModal(false)}
        >
          <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
            <h2>📤 Upload Albums to Firebase Storage</h2>

            {!uploading ? (
              <div className="upload-instructions">
                <p>
                  <strong>{selectedAlbums.size} albums</strong> selected for
                  upload
                </p>

                <div className="expected-paths">
                  <p>
                    <strong>Expected Local Paths:</strong>
                  </p>
                  <div className="paths-list">
                    {unavailableAlbums
                      .filter((a) => selectedAlbums.has(a.id))
                      .slice(0, 5)
                      .map((album) => {
                        const localPath =
                          album.type === 'Singles'
                            ? `D:\\Jukebox\\Singles\\${album.language}\\Various\\Variety\\Mix\\${album.album}\\`
                            : `D:\\Jukebox\\${album.type}\\${album.language}\\${album.genre}\\${album.subgenre}\\${album.artist}\\${album.album}\\`;
                        return (
                          <div key={album.id} className="path-item">
                            📁 {localPath}
                          </div>
                        );
                      })}
                    {selectedAlbums.size > 5 && (
                      <div className="path-item more">
                        ... and {selectedAlbums.size - 5} more albums
                      </div>
                    )}
                  </div>
                </div>

                <p>
                  Select your <strong>D:\Jukebox\</strong> folder when prompted:
                </p>

                <input
                  type="file"
                  webkitdirectory="true"
                  directory="true"
                  multiple
                  onChange={handleFolderUpload}
                  style={{ display: 'none' }}
                  id="folder-input"
                />

                <label htmlFor="folder-input" className="folder-select-btn">
                  📁 Browse to D:\Jukebox
                </label>

                <div className="upload-note">
                  <strong>💡 Tip:</strong> Navigate to your{' '}
                  <code>D:\Jukebox</code> folder and select it. The uploader
                  will automatically find and upload all selected albums.
                </div>

                <button
                  onClick={() => setShowUploadModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="upload-progress-container">
                <p className="upload-status">{uploadStatus}</p>

                <div className="progress-items">
                  {Object.entries(uploadProgress).map(([albumId, progress]) => (
                    <div key={albumId} className="progress-item">
                      <div className="progress-info">
                        <span className="progress-file">
                          {progress.fileName}
                        </span>
                        <span className="progress-count">
                          {progress.current} / {progress.total} files
                        </span>
                      </div>
                      <div className="progress-bar">
                        <div
                          className="progress-bar-fill"
                          style={{ width: `${progress.progress}%` }}
                        />
                      </div>
                      <span className="progress-percent">
                        {Math.round(progress.progress)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Grouped Albums */}
      <div className="upload-groups">
        {Object.keys(groupedAlbums)
          .sort()
          .map((groupName) => {
            const albums = groupedAlbums[groupName];
            const allSelected = albums.every((a) => selectedAlbums.has(a.id));
            const someSelected = albums.some((a) => selectedAlbums.has(a.id));

            return (
              <div key={groupName} className="upload-group">
                <div className="group-header">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(input) =>
                      input &&
                      (input.indeterminate = someSelected && !allSelected)
                    }
                    onChange={() => toggleGroup(albums)}
                  />
                  <h3>{groupName}</h3>
                  <span className="group-count">({albums.length} albums)</span>
                  <button
                    className="group-select-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGroup(albums);
                    }}
                  >
                    {allSelected ? '✓ Selected' : 'Select All'}
                  </button>
                </div>

                <div className="group-albums">
                  {albums.map((album) => {
                    const localPath =
                      album.type === 'Singles'
                        ? `D:\\Jukebox\\Singles\\${album.language}\\Various\\Variety\\Mix\\${album.album}\\`
                        : `D:\\Jukebox\\${album.type}\\${album.language}\\${album.genre}\\${album.subgenre}\\${album.artist}\\${album.album}\\`;
                    return (
                      <div key={album.id} className="album-item">
                        <input
                          type="checkbox"
                          checked={selectedAlbums.has(album.id)}
                          onChange={() => toggleAlbum(album.id)}
                        />
                        <div className="album-info">
                          <div className="album-title">
                            {album.artist} - {album.album}
                          </div>
                          <div className="album-meta">
                            {album.type} • {album.language} • {album.genre}/
                            {album.subgenre}
                          </div>
                          <div
                            className="album-path"
                            title="Expected local path"
                          >
                            📁 {localPath}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
      </div>

      {filteredAlbums.length === 0 && (
        <div className="no-results">
          <p>
            {unavailableAlbums.length === 0
              ? '🎉 No unavailable albums found! All albums are available in Storage.'
              : `🔍 No unavailable albums found with current filters!\n\nTotal unavailable albums: ${unavailableAlbums.length}\nTry adjusting your filters or click "Clear Filters" below.`}
          </p>
          {unavailableAlbums.length > 0 && (
            <button
              onClick={() => {
                setFilters({
                  type: 'all',
                  language: 'all',
                  genre: 'all',
                  subgenre: 'all',
                  artist: 'all',
                  album: 'all',
                });
              }}
              className="btn btn-primary"
              style={{ marginTop: '10px' }}
            >
              🗑️ Clear All Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default UploadManager;
